// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseURL: 'http://localhost:8080' | (string & {});
};

export type AccountStandingResponse = {
    awaitingReenrolment: boolean;
    locked: boolean;
    twoFactorOn: boolean;
};

export enum ActionActorType {
    USER = 'USER',
    SYSTEM = 'SYSTEM'
}

export type ActivationResponse = {
    membershipStarted: boolean;
};

export type Actor = {
    role: Role;
    type: ActionActorType;
    userId?: number | null;
};

export type AddBoardMemberRequest = {
    description?: string | null;
    /**
     * Who held the place, when no account can be attached to it
     */
    displayName?: string | null;
    endDate?: string | null;
    /**
     * The name the member was known by, without the quotes around it
     */
    nickname?: string | null;
    /**
     * Where the portrait is stored; blank leaves the member without one
     */
    portrait?: string | null;
    role: string;
    startDate: string;
    /**
     * The account holding the membership; absent for somebody with no account
     */
    userId?: number | null;
};

/**
 * Put somebody on a team's roster for a season
 */
export type AddRosterEntryRequest = {
    /**
     * A short caption about them, in markdown
     */
    description?: string | null;
    displayName?: string | null;
    /**
     * The game the team is fielded in; naming somebody fields it there
     */
    game: string;
    handle: string;
    /**
     * Where this entry's picture is stored; nothing leaves it without one
     */
    icon?: string | null;
    role: TeamRole;
    /**
     * What they did in the team's own words, beside the fixed part
     */
    roleTitle?: string | null;
    seasonId: number;
    /**
     * The member this entry belongs to, when they are known
     */
    userId?: number | null;
};

export type AddressResponse = {
    city?: string | null;
    country?: string | null;
    createdAt: string;
    houseNumber?: string | null;
    id: number;
    /**
     * False where the address could not be opened now, or is one of many in a list: its fields are then empty
     */
    opened: boolean;
    street?: string | null;
    updatedAt: string;
    userId?: number | null;
    version: number;
    zipCode?: string | null;
};

export enum AnnounceChoice {
    NOW = 'NOW',
    NEXT_MORNING = 'NEXT_MORNING'
}

export type AnswerRequest = {
    optionSelections?: Array<boolean> | null;
    questionId: number;
    textResponse?: string | null;
};

export type AnswerResponse = {
    createdAt: string;
    id: number;
    optionSelections?: Array<boolean> | null;
    questionId: number;
    textResponse?: string | null;
    updatedAt: string;
    version: number;
};

/**
 * Problem Details for HTTP APIs including validation errors.
 */
export type ApiError = {
    /**
     * Human-readable explanation specific to this occurrence.
     */
    detail?: string;
    /**
     * List of field/object validation errors (present when binding/validation fails).
     */
    errors?: Array<FieldValidationError>;
    /**
     * A URI reference that identifies the specific occurrence.
     */
    instance?: string;
    /**
     * HTTP status code.
     */
    status?: number;
    /**
     * Short, human-readable summary of the problem.
     */
    title?: string;
    /**
     * Trace or correlation id if available (Spring may add this via problem detail handlers).
     */
    traceId?: string;
    /**
     * Problem type URI (RFC 7807).
     */
    type?: string;
};

export type ArchiveCommitteeRequest = {
    archived: boolean;
};

/**
 * Whether nobody plays a game casually any more
 */
export type ArchiveGameRequest = {
    archived: boolean;
};

/**
 * What the association can say about itself in numbers
 */
export type AssociationStatisticsResponse = {
    /**
     * Boards that have sat
     */
    boards: number;
    /**
     * Committees that run
     */
    committees: number;
    /**
     * Events over the last rolling year
     */
    eventsLastYear: number;
    /**
     * Games with a team standing in them now
     */
    gamesPlayed: number;
    /**
     * Seasons that had a team fielded in them
     */
    seasonsPlayed: number;
    /**
     * Teams standing this season
     */
    teamsThisSeason: number;
};

export type BackupCodesResponse = {
    codes: Array<string>;
};

/**
 * The association's own bank account, as a member needs it to make a transfer
 */
export type BankAccountResponse = {
    accountName: string;
    bic: string;
    iban: string;
};

export type BlogResponse = {
    createdAt: string;
    html: string;
    id: number;
    publishedAt: string;
    title: string;
    updatedAt: string;
    url: string;
    version: number;
};

export type BoardCreateMembershipRequest = {
    endDate?: string | null;
    incasso: boolean;
    memberType: MemberType;
    startDate: string | null;
    userId: number;
};

export type BoardMemberResponse = {
    boardId: number;
    createdAt: string;
    /**
     * The personal note the board page shows beside a member
     */
    description?: string | null;
    endDate?: string | null;
    /**
     * The membership's own id, which is how it is edited or removed
     */
    id: number;
    /**
     * Who held the place: the linked account's name, or the recorded one
     */
    name?: string | null;
    /**
     * The name the member was known by, beside the name rather than inside it
     */
    nickname?: string | null;
    /**
     * The member's portrait, with its size and the widths it is stored at
     */
    portrait?: Image | null;
    role: string;
    startDate: string;
    updatedAt: string;
    /**
     * The account holding the membership, when one is linked to it
     */
    userId?: number | null;
    version: number;
};

/**
 * A board as a write says it stands, whether it makes one or edits one
 */
export type BoardRequest = {
    /**
     * The board's own colour; blank means the association's blue
     */
    accent?: string | null;
    /**
     * Kept for the column behind it; the board's own name is used when blank
     */
    candidate?: string | null;
    /**
     * The board's shouted line
     */
    cheer?: string | null;
    /**
     * What the year was about, in the board's own words
     */
    description?: string | null;
    endDate?: string | null;
    /**
     * The name the board chose for itself; blank for a board with none
     */
    name?: string | null;
    /**
     * The board's place in the line; the ninth board is 9
     */
    number: number;
    /**
     * Where the board's group photograph is stored; blank leaves it without one
     */
    photo?: string | null;
    startDate: string;
    /**
     * The version the edit was made against; a stale one is refused. Ignored on create
     */
    version?: number | null;
};

export type BoardResponse = {
    /**
     * The board's own colour; absent means the association's blue
     */
    accent?: string | null;
    candidate: string;
    /**
     * The board's shouted line
     */
    cheer?: string | null;
    createdAt: string;
    /**
     * What the year was about, in the board's own words
     */
    description?: string | null;
    endDate?: string | null;
    id: number;
    members: Array<BoardMemberResponse>;
    /**
     * The name the board chose for itself, where one is recorded
     */
    name?: string | null;
    /**
     * The board's place in the line, unique among the boards that exist
     */
    number: number;
    /**
     * The board's group photograph, with its size and the widths it is stored at
     */
    photo?: Image | null;
    startDate: string;
    updatedAt: string;
    version: number;
};

export type BulkActionResult = {
    applied: number;
    queued: number;
    skipped: number;
};

export type BulkContributionEmailPreviewRequest = {
    contributionPeriodId: number | null;
    userIds: Array<number>;
};

export type BulkContributionEmailPreviewResponse = {
    contributionPeriodId: number;
    rows: Array<BulkContributionEmailRowResponse>;
    /**
     * Selected ids that resolve to nobody. No row is drawn, and the send refuses them.
     */
    unknownUserIds: Array<number>;
};

export type BulkContributionEmailRowResponse = {
    /**
     * Follows from the fee type and the period. Never typed.
     */
    amount?: number | null;
    /**
     * The email this member gets unless the treasurer switches it.
     */
    defaultKind: ContributionEmailKind;
    disposition: BulkRowDisposition;
    /**
     * Absent only for honorary members, who owe nothing.
     */
    feeType?: BulkFeeType | null;
    lastNotifiedOn?: string | null;
    lastRemindedOn?: string | null;
    /**
     * Start of the membership every decision on this row was judged against.
     */
    memberSince?: string | null;
    memberType: MemberType;
    name: string;
    /**
     * Why this member is warned about or not written to.
     */
    reason?: BulkRowReason | null;
    userId: number;
};

export enum BulkFeeType {
    FULL_YEAR_FEE = 'FULL_YEAR_FEE',
    HALF_YEAR_FEE = 'HALF_YEAR_FEE',
    ALUMNI_FEE = 'ALUMNI_FEE'
}

export type BulkMarkPaidRequest = {
    contributionPeriodId: number | null;
    userIds: Array<number>;
};

export type BulkMarkUnpaidRequest = {
    contributionPeriodId: number | null;
    userIds: Array<number>;
};

export type BulkMembershipPreview = {
    effectiveDate: string;
    rows: Array<BulkMembershipPreviewRow>;
};

export type BulkMembershipPreviewRow = {
    disposition: BulkRowDisposition;
    reason?: BulkRowReason | null;
    userId: number;
};

export type BulkMembershipRequest = {
    userIds: Array<number>;
};

/**
 * Where to file several targets at once.
 */
export type BulkMoveTargetsRequest = {
    /**
     * The external ids of the targets to move, as the system itself names them.
     */
    externalIds: Array<string>;
    /**
     * The folder to move them into. Must already exist in the system.
     */
    folder: string;
};

export enum BulkRowDisposition {
    INCLUDED = 'INCLUDED',
    SKIPPED = 'SKIPPED',
    EXCLUDED = 'EXCLUDED',
    WARNING = 'WARNING'
}

export enum BulkRowReason {
    ALREADY_PAID = 'ALREADY_PAID',
    NOT_PAID = 'NOT_PAID',
    HONORARY = 'HONORARY',
    INCASSO_MISMATCH = 'INCASSO_MISMATCH',
    NO_ACTIVE_MEMBERSHIP = 'NO_ACTIVE_MEMBERSHIP',
    NOT_MEMBER_IN_PERIOD = 'NOT_MEMBER_IN_PERIOD',
    STARTED_TODAY = 'STARTED_TODAY',
    NO_EMAIL = 'NO_EMAIL',
    DELETED = 'DELETED',
    ALREADY_ACTIVE = 'ALREADY_ACTIVE',
    NO_CONTRIBUTION_PERIOD = 'NO_CONTRIBUTION_PERIOD',
    WILL_RESUME = 'WILL_RESUME',
    WILL_START_NEW = 'WILL_START_NEW'
}

/**
 * Contract-only holder that publishes the bulk-action enums. Not returned by any endpoint.
 */
export type BulkRowVocabulary = {
    disposition: BulkRowDisposition;
    feeType: BulkFeeType;
    reason: BulkRowReason;
};

/**
 * The outcome of moving several targets.
 */
export type BulkTargetMoveResult = {
    /**
     * The targets the system refused, and what it said. Empty when all moved.
     */
    failed: Array<FailedTargetMove>;
    /**
     * The targets that were moved, as the system now describes them.
     */
    moved: Array<ExternalTarget>;
};

/**
 * A game as the board adds or corrects it from the casual pages
 */
export type CasualGameRequest = {
    /**
     * The colour that carries this game, as # and six hex digits, or nothing for the island's own
     */
    accent?: string | null;
    /**
     * Where the game's banner is stored; nothing takes it away
     */
    banner?: string | null;
    /**
     * The Discord channels it lives in; left out, the ones it has are kept
     */
    channels?: Array<GameChannelRequest> | null;
    /**
     * What the competition pages say; blank to say the intro
     */
    competitionIntro?: string | null;
    /**
     * The Discord channels its esports players meet in; left out, the ones it has are kept
     */
    esportsChannels?: Array<GameChannelRequest> | null;
    /**
     * Where the game's icon is stored; nothing takes it away
     */
    icon?: string | null;
    intro?: string | null;
    /**
     * What this game is called. Its code is taken from it once, when it is added
     */
    name: string;
    /**
     * The address this game answers to under /casual
     */
    slug: string;
    /**
     * Where the game sits among the others; left out, a new game goes last and a game keeps its place
     */
    sortIndex?: number | null;
};

/**
 * A game as the casual pages show it
 */
export type CasualGameResponse = {
    /**
     * The colour that carries this game, where one has been chosen
     */
    accent?: string | null;
    /**
     * Nobody plays it casually any more; it is among the games we used to play
     */
    archived: boolean;
    /**
     * The game's own image
     */
    banner?: Image | null;
    /**
     * The Discord channels it is talked about in, in the order chosen
     */
    channels: Array<GameChannelResponse>;
    /**
     * The identifier everything else files the game under. Never changes
     */
    code: string;
    /**
     * What the competition pages say instead of the intro, where anything is said
     */
    competitionIntro?: string | null;
    /**
     * The Discord channels its esports players meet in, in the order chosen
     */
    esportsChannels: Array<GameChannelResponse>;
    /**
     * The game's own icon
     */
    icon?: Image | null;
    /**
     * A team is fielded in it this season
     */
    inCompetition: boolean;
    /**
     * What is said about the game, where anything is said
     */
    intro?: string | null;
    /**
     * What this game is called
     */
    name: string;
    /**
     * The address this game answers to under /casual
     */
    slug: string;
    /**
     * Where the game sits among the others
     */
    sortIndex: number;
};

export type CodeRequest = {
    code: string;
};

export type CohortDetail = {
    /**
     * Which definition in code decides who belongs to this cohort
     */
    definitionKey?: string | null;
    externalId?: string | null;
    folder?: string | null;
    id: number;
    kind: CohortKind;
    label: string;
    memberCount: number;
    members: Array<CohortMemberRow>;
    system: string;
};

export enum CohortKind {
    LIST = 'LIST',
    ROLE = 'ROLE',
    GROUP = 'GROUP'
}

export type CohortMapping = {
    cohortId: number;
    externalId?: string | null;
    kind: CohortKind;
    label: string;
    /**
     * When this cohort was last confirmed to agree with its target
     */
    lastReconciledAt?: string | null;
    /**
     * Where the target sits on its system, outside in: the system, then any folder holding it. Read from what was recorded when the target was linked or moved, so a page costs no call to the system.
     */
    path: Array<string>;
    /**
     * External system this mapping targets
     */
    system: TargetSystem;
};

export type CohortMemberRow = {
    cohortMemberId: number;
    isUserDeleted: boolean;
    joinedAt: string;
    userEmail?: string | null;
    userFullName?: string | null;
    userId: number;
};

export type CohortRepair = {
    cohortId: number;
    enqueuedAdds: number;
};

export enum CohortSubjectCategory {
    COMMITTEES = 'COMMITTEES',
    PERIODS = 'PERIODS',
    MEMBERS = 'MEMBERS'
}

export type CohortSubjectDetail = {
    category: CohortSubjectCategory;
    /**
     * Which definition in code decides who belongs here
     */
    definitionKey?: string | null;
    description?: string | null;
    id: number;
    label: string;
    mappings: Array<CohortMapping>;
    members: Array<CohortSubjectMember>;
    /**
     * True when no definition produces this cohort any more
     */
    orphaned: boolean;
    type: CohortSubjectType;
};

export type CohortSubjectMember = {
    cohortMemberId: number;
    /**
     * What the external system calls this row
     */
    externalLabel?: string | null;
    /**
     * The row's identity in the external system, where it has one
     */
    externalUserId?: string | null;
    isUserDeleted: boolean;
    joinedAt: string;
    /**
     * Whether this row is in step with the external system
     */
    state?: 'DESIRED' | 'SYNCED' | 'VERIFIED' | 'STRANGER' | 'INVALID';
    /**
     * Which system's ledger this row belongs to
     */
    system?: TargetSystem | null;
    userEmail?: string | null;
    userFullName?: string | null;
    /**
     * Null for a row present externally with no local account
     */
    userId?: number | null;
};

export type CohortSubjectSummary = {
    category: CohortSubjectCategory;
    id: number;
    label: string;
    mappingCount: number;
    memberCount: number;
    type: CohortSubjectType;
};

export enum CohortSubjectType {
    COMMITTEE_MEMBERS = 'COMMITTEE_MEMBERS',
    PERIOD_PAYERS = 'PERIOD_PAYERS',
    PERIOD_MEMBERS = 'PERIOD_MEMBERS',
    PERIOD_ACTIVE_MEMBERS = 'PERIOD_ACTIVE_MEMBERS',
    NEWSLETTER_SUBSCRIBERS = 'NEWSLETTER_SUBSCRIBERS'
}

export type CohortSummary = {
    externalId?: string | null;
    folder?: string | null;
    id: number;
    kind: CohortKind;
    label: string;
    memberCount: number;
    system: string;
};

/**
 * What deleting a committee hands over to the committee that takes over
 */
export type CommitteeDeletionResponse = {
    /**
     * How many live events move to the committee that takes over
     */
    events: number;
};

export type CommitteeMemberRequest = {
    /**
     * What this member does on the committee. Omitted for a member who simply sits on it, which is most of them.
     */
    role?: string | null;
    userId: number;
};

export type CommitteeMemberResponse = {
    committeeId: number;
    createdAt: string;
    role?: string | null;
    updatedAt: string;
    userId: number;
    version: number;
};

export type CommitteeOwnPageRequest = {
    banner?: string | null;
    description: string;
    gameCodes?: Array<string> | null;
    icon?: string | null;
    version?: number | null;
};

export type CommitteePageResponse = {
    archived: boolean;
    /**
     * Since when it no longer runs; absent while it runs
     */
    archivedAt?: string | null;
    banner?: Image | null;
    description: string;
    gameCodes: Array<string>;
    /**
     * Its logo, absent for none
     */
    icon?: Image | null;
    id: number;
    members: Array<CommitteeSeatResponse>;
    name: string;
    slug: string;
};

export type CommitteeResponse = {
    /**
     * Whether the committee no longer runs
     */
    archived: boolean;
    /**
     * Since when it no longer runs; absent while it runs
     */
    archivedAt?: string | null;
    banner?: Image | null;
    createdAt: string;
    description: string;
    /**
     * The codes of the games the committee organises events for
     */
    gameCodes: Array<string>;
    /**
     * Its logo, absent for none
     */
    icon?: Image | null;
    id: number;
    /**
     * Who sits on it, for the board and its own members; absent for anybody else
     */
    members?: Array<CommitteeMemberResponse> | null;
    name: string;
    /**
     * The address the committee's page answers to
     */
    slug: string;
    updatedAt: string;
    version: number;
};

export type CommitteeSeatResponse = {
    /**
     * Their Discord avatar's address; absent where Discord is not linked
     */
    avatar?: string | null;
    /**
     * Whether the name is their Discord name
     */
    discord: boolean;
    /**
     * The name the Discord server shows them by, or their username where Discord is not linked
     */
    name: string;
    role?: string | null;
};

export enum ContributionEmailKind {
    REMINDER = 'REMINDER',
    INCASSO_NOTIFICATION = 'INCASSO_NOTIFICATION'
}

export type ContributionEmailMessageResponse = {
    feeType: BulkFeeType;
    html: string;
    kind: ContributionEmailKind;
    recipientEmail: string;
    recipientName: string;
    subject: string;
};

export type ContributionPeriodResponse = {
    alumniFee: number;
    contactListId?: number | null;
    createdAt: string;
    endDate: string;
    fullYearFee: number;
    halfYearCutoffDate: string;
    halfYearFee: number;
    id: number;
    startDate: string;
    updatedAt: string;
    version: number;
};

export type ContributionReminderResponse = {
    /**
     * The amount this ask asked for, as stated. Absent wherever the fee type is.
     */
    amount?: number | null;
    /**
     * When the member was asked.
     */
    askedAt: string;
    contributionPeriodId: number;
    createdAt: string;
    /**
     * The fee type this ask stated. Absent when it quoted the period's options instead.
     */
    feeType?: BulkFeeType | null;
    id: number;
    /**
     * The date this ask asked to be paid by. Absent wherever the fee type is.
     */
    paymentDueDate?: string | null;
    updatedAt: string;
    userId: number;
    version: number;
};

export type ContributionResponse = {
    contributionPeriodId: number;
    createdAt: string;
    remindedAt?: string | null;
    updatedAt: string;
    userId: number;
    version: number;
};

export type CreateAddressRequest = {
    city: string;
    country: string;
    houseNumber: string;
    street: string;
    userId: number;
    zipCode: string;
};

export type CreateBlogRequest = {
    html: string;
    publishedAt: string;
    title: string;
};

export type CreateCommitteeRequest = {
    /**
     * Where its stored banner is, or absent for none
     */
    banner?: string | null;
    description: string;
    gameCodes: Array<string>;
    /**
     * Where its stored logo is, or absent for none
     */
    icon?: string | null;
    members: Array<CommitteeMemberRequest>;
    name: string;
    /**
     * The address its page answers to; absent makes one from the name
     */
    slug?: string | null;
};

export type CreateContributionPeriodRequest = {
    alumniFee: number;
    contactListId?: number | null;
    endDate: string;
    fullYearFee: number;
    /**
     * A regular membership starting after this date pays the half-year fee.
     */
    halfYearCutoffDate: string;
    halfYearFee: number;
    startDate: string;
};

export type CreateContributionReminderRequest = {
    contributionPeriodId: number;
    userId: number;
};

export type CreateContributionRequest = {
    contributionPeriodId: number;
    userId: number;
};

export type CreateEventRequest = {
    /**
     * When the events-info post goes out, which approving must say while it is not out yet
     */
    announce?: AnnounceChoice | null;
    approved: boolean;
    banner?: EventBannerRequest | null;
    committeeId: number;
    description: string;
    endTime: string;
    /**
     * The codes of the games the event names
     */
    gameCodes: Array<string>;
    location?: string | null;
    memberPrice?: number | null;
    membersOnly: boolean;
    pingedRoles: Array<PingedRoleRequest>;
    publicPrice?: number | null;
    signUp: boolean;
    signUpDeadline?: string | null;
    signUpForm?: SurveyRequest | null;
    signUpLimit?: number | null;
    startTime: string;
    title: string;
};

export type CreateEventSignUpRequest = {
    answers?: Array<AnswerRequest> | null;
    guest?: CreateGuestRequest | null;
    userId?: number | null;
};

export type CreateGuestRequest = {
    discord: string;
    email: string;
    name: string;
    phoneNumber?: string | null;
    version?: number | null;
};

export type CreateMemberProfileRequest = {
    bhv: boolean;
    dateOfBirth: string;
    ehbo: boolean;
    gender?: string | null;
    /**
     * Whether this member's real name may appear in a roster
     */
    nameOnRosters: boolean;
    nationality: string;
    studentNumber?: string | null;
    userId: number;
};

export type CreateSponsorRequest = {
    description: string;
    name: string;
};

export type CreateTarget = {
    folderHint?: string | null;
    label: string;
    system: TargetSystem;
};

export type CreateTelemetryRequest = {
    platform: PlatformType;
    url: string;
};

export type CreateUserRequest = {
    consentPrivacy?: boolean | null;
    discord: string;
    /**
     * The Discord user ID of the member picked from the server, where one is picked
     */
    discordId?: string | null;
    email: string;
    firstName: string;
    fullName?: string | null;
    initials: string;
    lastName: string;
    memberProfile?: UpsertMemberProfileRequest | null;
    newsletter: boolean;
    password?: string | null;
    phoneNumber: string;
    photoConsent?: boolean | null;
    prefix?: string | null;
    username: string;
};

export type CsrfToken = {
    headerName?: string;
    parameterName?: string;
    token?: string;
};

export type DerivedRoleResponse = {
    role: Role;
    source: RoleSource;
};

export enum DescriptionCellAlign {
    LEFT = 'LEFT',
    CENTER = 'CENTER',
    RIGHT = 'RIGHT'
}

export type DescriptionNode = {
    align?: DescriptionCellAlign | null;
    /**
     * Whether a server emoji moves
     */
    animated?: boolean | null;
    children: Array<DescriptionNode>;
    /**
     * Where the node ends in the text, exclusive
     */
    end: number;
    /**
     * Whether a cell heads its column
     */
    header?: boolean | null;
    /**
     * Where a link leads, or where a picture is
     */
    href?: string | null;
    /**
     * The Discord ID a mention or a server emoji names
     */
    id?: string | null;
    kind: DescriptionNodeKind;
    /**
     * The language a code block names
     */
    language?: string | null;
    /**
     * A heading's level, from 1
     */
    level?: number | null;
    /**
     * A server emoji's name
     */
    name?: string | null;
    /**
     * The number an ordered list counts from
     */
    number?: number | null;
    ordered?: boolean | null;
    /**
     * Where the node starts in the text, counted in UTF-16 code units
     */
    start: number;
    style?: DescriptionTimeStyle | null;
    /**
     * The words of a text or code, the character of an emoji, or a picture's alternative text
     */
    text?: string | null;
    /**
     * Whether a list's items are lines rather than paragraphs
     */
    tight?: boolean | null;
    title?: string | null;
    /**
     * A timestamp's moment, in seconds since 1970
     */
    unix?: number | null;
};

export enum DescriptionNodeKind {
    PARAGRAPH = 'PARAGRAPH',
    HEADING = 'HEADING',
    SUBTEXT = 'SUBTEXT',
    QUOTE = 'QUOTE',
    LIST = 'LIST',
    ITEM = 'ITEM',
    CODE_BLOCK = 'CODE_BLOCK',
    RULE = 'RULE',
    TABLE = 'TABLE',
    ROW = 'ROW',
    CELL = 'CELL',
    TEXT = 'TEXT',
    LINE_BREAK = 'LINE_BREAK',
    STRONG = 'STRONG',
    EMPHASIS = 'EMPHASIS',
    UNDERLINE = 'UNDERLINE',
    STRIKE = 'STRIKE',
    SPOILER = 'SPOILER',
    CODE = 'CODE',
    LINK = 'LINK',
    IMAGE = 'IMAGE',
    EMOJI = 'EMOJI',
    SERVER_EMOJI = 'SERVER_EMOJI',
    USER_MENTION = 'USER_MENTION',
    ROLE_MENTION = 'ROLE_MENTION',
    CHANNEL_MENTION = 'CHANNEL_MENTION',
    TIMESTAMP = 'TIMESTAMP'
}

/**
 * A description being written, not yet saved
 */
export type DescriptionPreviewRequest = {
    text: string;
};

export enum DescriptionTimeStyle {
    SHORT_TIME = 'SHORT_TIME',
    LONG_TIME = 'LONG_TIME',
    SHORT_DATE = 'SHORT_DATE',
    LONG_DATE = 'LONG_DATE',
    SHORT_DATE_TIME = 'SHORT_DATE_TIME',
    LONG_DATE_TIME = 'LONG_DATE_TIME',
    RELATIVE = 'RELATIVE'
}

export type DescriptionTree = {
    /**
     * The description's length as stored, which the cap counts
     */
    length: number;
    nodes: Array<DescriptionNode>;
};

/**
 * A text channel a game may live in
 */
export type DiscordChannelResponse = {
    /**
     * The server the channel is in, which a link into it needs
     */
    guildId: string;
    id: string;
    name: string;
};

export enum DiscordEmojiKind {
    SERVER = 'SERVER',
    APPLICATION = 'APPLICATION',
    SEEN = 'SEEN'
}

/**
 * A picture uploaded to the Discord server, which a description writes as <:name:id>
 */
export type DiscordEmojiResponse = {
    /**
     * Moving, written <a:name:id> rather than <:name:id>
     */
    animated: boolean;
    id: string;
    /**
     * Uploaded to the server or to the bot's Discord application, or seen used in the server
     */
    kind: DiscordEmojiKind;
    name: string;
    /**
     * How many messages in the server used a seen emoji; null for the others
     */
    uses?: number | null;
};

/**
 * The association's Discord server as the site shows it: counts and the voice rooms with who is in them
 */
export type DiscordLiveResponse = {
    /**
     * How many members the server has, where Discord says
     */
    members?: number | null;
    /**
     * How many members are online, where Discord says
     */
    online?: number | null;
    /**
     * The voice rooms everybody can see, in Discord's order
     */
    rooms: Array<DiscordVoiceRoomResponse>;
    /**
     * The server's name as Discord has it
     */
    server: string;
};

/**
 * A member of the Discord server, as a picker shows them
 */
export type DiscordMemberResponse = {
    /**
     * Their avatar's address
     */
    avatar: string;
    /**
     * Their Discord user ID, which never changes
     */
    id: string;
    /**
     * The name the server shows: their nickname there, else their display name, else their username
     */
    name: string;
    username: string;
};

/**
 * A channel everybody in the Discord server can see, which a description may mention
 */
export type DiscordMentionChannelResponse = {
    /**
     * The category the server files the channel under, where it has one
     */
    category?: string | null;
    id: string;
    name: string;
};

/**
 * What a description's mentions name: the members, roles and channels the server has
 */
export type DiscordMentionsResponse = {
    channels: Array<DiscordNameResponse>;
    roles: Array<DiscordRoleNameResponse>;
    users: Array<DiscordNameResponse>;
};

/**
 * A member or a channel of the Discord server, by the name the server shows
 */
export type DiscordNameResponse = {
    id: string;
    name: string;
};

/**
 * A role of the Discord server, as a mention shows it
 */
export type DiscordRoleNameResponse = {
    /**
     * The role's colour as 0xRRGGBB, where it has one
     */
    colour?: number | null;
    id: string;
    name: string;
};

/**
 * A role in the Discord server an event may ping
 */
export type DiscordRoleResponse = {
    /**
     * The role's colour as 0xRRGGBB, where it has one
     */
    colour?: number | null;
    id: string;
    name: string;
};

/**
 * The voice rooms the viewer's own Discord member may join
 */
export type DiscordViewerRoomsResponse = {
    /**
     * The IDs of the voice rooms that member may join
     */
    joinable: Array<string>;
    /**
     * Whether the viewer's account is linked to a member of the server
     */
    linked: boolean;
};

/**
 * Somebody in a voice room
 */
export type DiscordVoicePersonResponse = {
    /**
     * Their avatar's address, where they have one
     */
    avatar?: string | null;
    name: string;
};

/**
 * A voice room everybody can see, with who is in it
 */
export type DiscordVoiceRoomResponse = {
    /**
     * The address that opens the room in Discord
     */
    href: string;
    id: string;
    /**
     * Everybody can see the room, but only some may join it
     */
    locked: boolean;
    name: string;
    people: Array<DiscordVoicePersonResponse>;
};

export type Email = {
    attempts?: number | null;
    createdAt?: string | null;
    deliveredAt?: string | null;
    deliveryStatus?: EmailDeliveryStatus | null;
    emailType?: string | null;
    errorReason?: string | null;
    errorType?: string | null;
    id?: number | null;
    jobExecutionId?: number | null;
    messageId?: string | null;
    openedAt?: string | null;
    /**
     * Whether this email's body was stored, so it can be previewed
     */
    previewable: boolean;
    recipientEmail?: string | null;
    recipientName?: string | null;
    sentAt?: string | null;
    subject?: string | null;
    updatedAt?: string | null;
};

export type EmailAddressResponse = {
    email: string;
    pendingEmail?: string | null;
};

export type EmailChangeRequest = {
    email: string;
};

export enum EmailDeliveryStatus {
    PENDING = 'PENDING',
    SENT = 'SENT',
    DELIVERED = 'DELIVERED',
    OPENED = 'OPENED',
    BOUNCED = 'BOUNCED',
    FAILED = 'FAILED'
}

export type EmailStats = {
    bouncedCount: number;
    deliveredCount: number;
    failedCount: number;
    openedCount: number;
    pendingCount: number;
    sentCount: number;
    totalCount: number;
};

export type EnqueueJobRequest = {
    jobType: string;
    /**
     * Job payload fields keyed by name; shape depends on the job type
     */
    payload?: {
        [key: string]: unknown;
    } | null;
};

export type EventBannerRequest = {
    fileId: number;
    version?: number | null;
};

export type EventBannerResponse = {
    createdAt: string;
    eventId: number;
    fileId: number;
    image?: Image | null;
    updatedAt: string;
    version: number;
};

export type EventResponse = {
    /**
     * Whether the events-info post is out, so approving asks no choice of when it goes out. Answered for one event, absent in a list.
     */
    announced?: boolean | null;
    approved: boolean;
    awaitingReapproval: boolean;
    banner?: EventBannerResponse | null;
    committeeId?: number | null;
    createdAt: string;
    description?: string | null;
    /**
     * The description as read, for one event; absent in a list.
     */
    descriptionTree?: DescriptionTree | null;
    endTime: string;
    /**
     * The codes of the games the event names
     */
    gameCodes: Array<string>;
    id: number;
    location?: string | null;
    memberPrice?: number | null;
    membersOnly: boolean;
    /**
     * The Discord roles the bot notifies when it posts the event
     */
    pingedRoles: Array<PingedRoleResponse>;
    publicPrice?: number | null;
    signUp: boolean;
    signUpCount: number;
    signUpDeadline?: string | null;
    signUpForm?: SurveyResponse | null;
    signUpLimit?: number | null;
    startTime: string;
    title: string;
    updatedAt: string;
    version: number;
};

/**
 * Who signed up for an event, as its page shows it
 */
export type EventRosterResponse = {
    /**
     * How many guests signed up without an account; they are counted, never named
     */
    guests: number;
    /**
     * The people signed up with an account, first sign-up first
     */
    people: Array<RosterPersonResponse>;
};

export enum EventSignUpKind {
    GUEST = 'GUEST',
    NON_MEMBER = 'NON_MEMBER',
    MEMBER = 'MEMBER'
}

export type EventSignUpResponse = {
    answers: Array<AnswerResponse>;
    createdAt: string;
    eventId: number;
    guest?: GuestResponse | null;
    id: number;
    kind: EventSignUpKind;
    updatedAt: string;
    user?: UserSummaryResponse | null;
    version: number;
};

export type ExternalTarget = {
    externalId: string;
    folderLabel?: string | null;
    kind: CohortKind;
    label: string;
    linkedCohortId?: number | null;
    memberCount?: number | null;
    path: Array<string>;
    system: TargetSystem;
};

/**
 * One target the external system would not move.
 */
export type FailedTargetMove = {
    externalId: string;
    label: string;
    /**
     * What the system said, for an operator to act on rather than a stack trace.
     */
    message: string;
};

/**
 * One member ranked by peak rate: their Discord tag and avatar when linked, otherwise their site username
 */
export type FastestResponse = {
    avatarUrl?: string | null;
    discordTag?: string | null;
    memberId: number;
    peakAt?: string | null;
    /**
     * The top rate the member reached across their devices, in pings per second
     */
    peakPps: number;
    rank: number;
    username?: string | null;
};

/**
 * Field a team in a game in a season, with or without the line-up it last had
 */
export type FieldTeamRequest = {
    /**
     * Where the art for this fielding is stored; nothing leaves what it has
     */
    banner?: string | null;
    /**
     * Which of this team's line-ups to copy across; wins over carryLineup
     */
    carryFrom?: LineupSourceRequest | null;
    /**
     * Copy the line-up this team last had in this game into this season
     */
    carryLineup: boolean;
    /**
     * The game it is being fielded in. A team may play more than one in a season
     */
    game: string;
};

/**
 * Details about a single field/object validation error.
 */
export type FieldValidationError = {
    /**
     * Validation code / constraint key.
     */
    code?: string;
    /**
     * Field that failed validation (null for global errors).
     */
    field?: string;
    /**
     * Human-readable validation message.
     */
    message?: string;
    /**
     * Object (target) name that failed validation.
     */
    objectName?: string;
    /**
     * Identifiers the error refers to when they are not numeric — an external system's own ids, for instance. The counterpart of `values`; an error carries one or the other, never both.
     */
    refs?: Array<string>;
    /**
     * Identifiers the error refers to, when the failing field carries a collection. Lets a client name the offending rows and reload them rather than restate the whole request.
     */
    values?: Array<number>;
};

/**
 * A team now fielded in a season, and whatever line-up came across with it
 */
export type FieldedTeamResponse = {
    /**
     * The art it is drawn with this season, carried across from the last one it played
     */
    banner?: Image | null;
    /**
     * The entries copied from the team's last season; empty when nothing was carried
     */
    carried: Array<RosterEntryResponse>;
    /**
     * The game it was fielded in
     */
    game: string;
    season: SeasonResponse;
    team: TeamResponse;
};

/**
 * A team fielded in a game in a season, which is where a line-up hangs
 */
export type FieldingResponse = {
    game: string;
    season: SeasonResponse;
};

export type FileResponse = {
    createdAt: string;
    id: number;
    mediaType: string;
    name: string;
    path: string;
    size?: number | null;
    type: FileType;
    updatedAt: string;
    version: number;
};

export enum FileType {
    DOCUMENT = 'DOCUMENT',
    PROFILE_PICTURE = 'PROFILE_PICTURE',
    EVENT_BANNER = 'EVENT_BANNER',
    COMMITTEE_BANNER = 'COMMITTEE_BANNER',
    COMMITTEE_ICON = 'COMMITTEE_ICON',
    EVENT_PICTURE = 'EVENT_PICTURE',
    SPONSOR_PICTURE = 'SPONSOR_PICTURE',
    GAME_BANNER = 'GAME_BANNER',
    GAME_ICON = 'GAME_ICON',
    TEAM_BANNER = 'TEAM_BANNER',
    TEAM_ICON = 'TEAM_ICON',
    ROSTER_ICON = 'ROSTER_ICON',
    BOARD_PHOTO = 'BOARD_PHOTO',
    BOARD_PORTRAIT = 'BOARD_PORTRAIT'
}

export type FirstContribution = {
    amount?: number | null;
    feeType?: BulkFeeType | null;
    membershipStartDate: string;
    periodEndDate?: string | null;
    periodId?: number | null;
    periodStartDate?: string | null;
};

/**
 * Set what a member is called in one game
 */
export type GameAccountRequest = {
    handle: string;
};

/**
 * What one member is called in one game
 */
export type GameAccountResponse = {
    game: string;
    handle: string;
    id: number;
    userId: number;
};

export enum GameChannelCategory {
    GAMES = 'GAMES',
    ESPORTS = 'ESPORTS'
}

/**
 * A Discord channel a game lives in, as the picker offered it
 */
export type GameChannelRequest = {
    guildId: string;
    id: string;
    name: string;
};

/**
 * A Discord channel a game lives in, with its name as last known
 */
export type GameChannelResponse = {
    /**
     * The server the channel is in, which a link into it needs
     */
    guildId: string;
    id: string;
    name: string;
};

/**
 * What removing a game would touch, so the offer to remove it can say so
 */
export type GameHoldingsResponse = {
    /**
     * Discord channels the game lives in
     */
    channels: number;
    /**
     * Committees linked to the game
     */
    committees: number;
    /**
     * Events that name the game
     */
    events: number;
    /**
     * People on those teams' line-ups
     */
    players: number;
    /**
     * Teams fielded in the game; a game with any cannot be removed
     */
    teams: number;
};

export type GameOrganisersRequest = {
    committeeIds: Array<number>;
};

/**
 * A game's teams for one season, and the seasons that can be shown
 */
export type GameRostersResponse = {
    game: string;
    /**
     * The season being shown; absent when the game has no rosters yet
     */
    season?: SeasonResponse | null;
    seasons: Array<SeasonResponse>;
    teams: Array<TeamRosterResponse>;
};

export type GuestResponse = {
    createdAt: string;
    discord: string;
    email: string;
    id: number;
    name: string;
    phoneNumber?: string | null;
    updatedAt: string;
    version: number;
};

/**
 * The SiteCie house line, a labelled total shown outside the member ranking
 */
export type HouseLineResponse = {
    label: string;
    peakAt?: string | null;
    /**
     * The top rate SiteCie reached across its replicas, in pings per second
     */
    peakPps: number;
    totalSent: number;
};

/**
 * An image a public page draws, and the widths it is stored at
 */
export type Image = {
    /**
     * How tall it is, absent where its size could not be read
     */
    height?: number | null;
    /**
     * Where it is stored, which is what a save points at to put it on a record
     */
    path: string;
    /**
     * The widths it is stored at, narrowest first
     */
    renditions: Array<ImageRendition>;
    /**
     * Where the full-size image is served
     */
    url: string;
    /**
     * How wide it is, absent where its size could not be read
     */
    width?: number | null;
};

/**
 * One stored width of an image
 */
export type ImageRendition = {
    /**
     * Where this width is served
     */
    url: string;
    width: number;
};

export type InboundReconcileApplyRequest = {
    previewToken: string;
    selectedExternalUserIds: Array<string>;
};

export type InboundReconcileApplyResponse = {
    acceptedCount: number;
    jobId?: number | null;
    skippedCount: number;
};

export type InboundReconcilePreview = {
    cohortLabel: string;
    definitionKey: string;
    matched: Array<InboundReconcileRow>;
    previewToken: string;
    remoteCount: number;
    skipped: Array<InboundReconcileRow>;
    writerSupported: boolean;
};

export type InboundReconcileRow = {
    alreadyMember: boolean;
    externalLabel?: string | null;
    externalUserId: string;
    reason?: 'DUPLICATE_REMOTE_ID' | 'MAPPING_CONFLICT' | 'DUPLICATE_USER_MATCH' | 'MAPPED_USER_INACTIVE' | 'UNMATCHED';
    userEmail?: string | null;
    userFullName?: string | null;
    userId?: number | null;
    writable: boolean;
};

export type JobCount = {
    channels: Array<string>;
    count: number;
    names: Array<string>;
    tally: JobTally;
};

export enum JobEffect {
    MADE = 'MADE',
    EDITED = 'EDITED',
    UNCHANGED = 'UNCHANGED',
    REMOVED = 'REMOVED'
}

export type JobExecution = {
    actor?: Actor | null;
    attempts: number | null;
    category?: JobExecutionCategory | null;
    createdAt?: string | null;
    dedupKey?: string | null;
    effect?: JobEffect | null;
    effectLink?: string | null;
    errorMessage?: string | null;
    errorReason?: string | null;
    errorType?: string | null;
    finishedAt?: string | null;
    foldedTriggers: Array<JobFoldedTrigger>;
    forced: boolean;
    id?: number | null;
    initiatedByDisplay?: string | null;
    initiatedByFullName?: string | null;
    initiatedByRole?: Role | null;
    initiatedByType?: ActionActorType | null;
    initiatedByUserId?: number | null;
    initiatedByUsername?: string | null;
    jobType: string | null;
    nextAttemptAt?: string | null;
    payload?: {
        [key: string]: unknown;
    } | null;
    queuedAt?: string | null;
    relatedEntities: Array<JobExecutionRelatedEntity>;
    report: Array<JobCount>;
    skipReason?: string | null;
    stackTrace?: string | null;
    startedAt?: string | null;
    status: JobExecutionStatus | null;
    targetSystem?: TargetSystem | null;
    trigger?: JobTrigger | null;
    updatedAt?: string | null;
};

export enum JobExecutionCategory {
    CALENDAR = 'calendar',
    CONTACT = 'contact',
    COHORT = 'cohort',
    DISCORD = 'discord',
    EMAIL = 'email',
    OTHER = 'other'
}

export type JobExecutionRelatedEntity = {
    id?: number | null;
    label: string;
    type: string;
};

export enum JobExecutionStatus {
    QUEUED = 'QUEUED',
    RUNNING = 'RUNNING',
    SUCCESS = 'SUCCESS',
    SKIPPED = 'SKIPPED',
    FAILED = 'FAILED',
    DEAD = 'DEAD'
}

export type JobFoldedTrigger = {
    at: string;
    initiatedByDisplay: string;
    initiatedByType: ActionActorType;
    initiatedByUserId?: number | null;
    trigger: JobTrigger;
};

export type JobPayloadField = {
    enumValues?: Array<string> | null;
    kind: JobPayloadFieldKind;
    name: string;
    required: boolean;
    type: string;
};

export enum JobPayloadFieldKind {
    PRIMITIVE = 'PRIMITIVE',
    ENUM = 'ENUM',
    OBJECT = 'OBJECT'
}

export type JobStatsDto = {
    avgSuccessDurationSeconds: number;
    deadCount: number;
    deadSinceStartup: number;
    failedCount: number;
    failedSinceStartup: number;
    queuedCount: number;
    recoveriesSinceStartup: number;
    runningCount: number;
    skippedCount: number;
    successCount: number;
    totalCount: number;
};

export enum JobTally {
    PLACES_READ = 'PLACES_READ',
    MESSAGES_READ = 'MESSAGES_READ',
    EMOJI_SEEN = 'EMOJI_SEEN',
    PLACES_LEFT = 'PLACES_LEFT',
    SHORTCODES_RESOLVED = 'SHORTCODES_RESOLVED',
    SHORTCODES_LEFT = 'SHORTCODES_LEFT',
    EVENTS_QUEUED = 'EVENTS_QUEUED',
    USERS_QUEUED = 'USERS_QUEUED',
    COHORTS_RECOMPUTED = 'COHORTS_RECOMPUTED',
    COHORTS_FAILED = 'COHORTS_FAILED',
    VALUES_SEALED = 'VALUES_SEALED',
    VALUES_REWRAPPED = 'VALUES_REWRAPPED'
}

export enum JobTrigger {
    EVENT_CREATED = 'EVENT_CREATED',
    EVENT_UPDATED = 'EVENT_UPDATED',
    EVENT_APPROVED = 'EVENT_APPROVED',
    EVENT_SENT_BACK = 'EVENT_SENT_BACK',
    EVENT_UNAPPROVED = 'EVENT_UNAPPROVED',
    EVENT_DELETED = 'EVENT_DELETED',
    SIGN_UPS_CHANGED = 'SIGN_UPS_CHANGED',
    USER_CHANGED = 'USER_CHANGED',
    USER_REMOVED = 'USER_REMOVED',
    MEMBERSHIP_CHANGED = 'MEMBERSHIP_CHANGED',
    SITE_ACTION = 'SITE_ACTION',
    MORNING_RUN = 'MORNING_RUN',
    SCHEDULED_RUN = 'SCHEDULED_RUN',
    ANOTHER_JOB = 'ANOTHER_JOB',
    BY_HAND = 'BY_HAND'
}

export type JobTypeDescriptor = {
    payloadFields: Array<JobPayloadField>;
    type: string;
};

export type JwtRequest = {
    password: string;
    username: string;
};

/**
 * The SiteCie house line, the members ranked by total sent and by peak rate, and the combined record
 */
export type LeaderboardResponse = {
    /**
     * The members ranked by the top rate they reached across their devices
     */
    fastest: Array<FastestResponse>;
    house?: HouseLineResponse | null;
    members: Array<StandingResponse>;
    /**
     * The top rate every sender reached together, SiteCie included; null when none was recorded
     */
    record?: RecordResponse | null;
};

/**
 * One person on a line-up being saved: an entry kept, or somebody added
 */
export type LineupEntryRequest = {
    /**
     * A short caption about them, in markdown
     */
    description?: string | null;
    displayName?: string | null;
    handle: string;
    /**
     * Where this entry's picture is stored; nothing takes the picture away
     */
    icon?: string | null;
    /**
     * The entry this stands for; nothing adds somebody new
     */
    id?: number | null;
    role: TeamRole;
    /**
     * What they did in the team's own words, beside the fixed part
     */
    roleTitle?: string | null;
    /**
     * The member this entry belongs to; nothing leaves it unattributed
     */
    userId?: number | null;
};

/**
 * One line-up of a team's: which game it was played in, and which season
 */
export type LineupSourceRequest = {
    game: string;
    seasonId: number;
};

export type LinkBoardMemberRequest = {
    /**
     * The account to attach to the membership; absent detaches it
     */
    userId?: number | null;
};

export type LinkExistingTarget = {
    externalId: string;
    system: TargetSystem;
};

/**
 * Attach a roster entry to a member, or detach it with a null id
 */
export type LinkRosterEntryRequest = {
    userId?: number | null;
};

export type LinkUser = {
    externalUserId: string;
    system: TargetSystem;
    userId: number;
};

export type LinkedUser = {
    externalUserId: string;
    system: TargetSystem;
    userId: number;
};

export type LockResponse = {
    contactEmail: string;
};

export type LoginResponse = {
    addressId?: number | null;
    roles: Array<Role>;
    twoFactor: TwoFactorStanding;
    userId: number;
    username: string;
};

export type MemberActivationRequest = {
    password: string;
    token: string;
    username: string;
};

export type MemberPeriodContribution = {
    dueDate?: string | null;
    endDate: string;
    fee?: number | null;
    feeType?: BulkFeeType | null;
    lastEmailAt?: string | null;
    lastEmailKind?: ContributionEmailKind | null;
    paidAt?: string | null;
    periodId: number;
    startDate: string;
};

export type MemberProfileResponse = {
    bhv: boolean;
    conditionsAcceptedAt?: string;
    createdAt: string;
    dateOfBirth?: string;
    ehbo: boolean;
    gender?: string;
    id: number;
    /**
     * Whether this member's real name may appear in a roster
     */
    nameOnRosters: boolean;
    nationality?: string;
    studentNumber?: string;
    updatedAt: string;
    userId: number;
    version: number;
};

export enum MemberType {
    ALUMNI = 'ALUMNI',
    HONORARY = 'HONORARY',
    REGULAR = 'REGULAR',
    NONE = 'NONE'
}

export type MembershipApplicationRequest = {
    conditionsAccepted: boolean;
};

export type MembershipResponse = {
    activatedOn?: string | null;
    createdAt: string;
    endDate?: string | null;
    id: number;
    incasso: boolean;
    memberType: MemberType;
    /**
     * Running and waiting for its first contribution, so it carries no member role yet.
     */
    pending: boolean;
    startDate: string;
    updatedAt: string;
    userId: number;
    version: number;
};

/**
 * Where to file a target.
 */
export type MoveTargetRequest = {
    /**
     * The folder to move the target into. Must already exist in the system.
     */
    folder: string;
};

export type NameOnRostersRequest = {
    shown: boolean;
};

export type OwnContributions = {
    held: boolean;
    paysByIncasso: boolean;
    periods: Array<MemberPeriodContribution>;
};

export type PageMetadata = {
    number?: number;
    size?: number;
    totalElements?: number;
    totalPages?: number;
};

export type PagedModelEmail = {
    content?: Array<Email>;
    page?: PageMetadata;
};

export type PagedModelEventResponse = {
    content?: Array<EventResponse>;
    page?: PageMetadata;
};

export type PagedModelJobExecution = {
    content?: Array<JobExecution>;
    page?: PageMetadata;
};

export type PagedModelUserDetailResponse = {
    content?: Array<UserDetailResponse>;
    page?: PageMetadata;
};

export type PasswordChangeRequest = {
    currentPassword: string;
    newPassword: string;
};

export type PasswordResetRequest = {
    password: string;
    token: string;
};

export type PaymentEmailsResultResponse = {
    incassoNotificationsSent: number;
    /**
     * Members in the selection who were not written to.
     */
    notWrittenTo: number;
    remindersSent: number;
};

/**
 * The activation email an account that has not been activated takes.
 */
export type PendingActivation = {
    /**
     * Which activation email applies to it.
     */
    purpose: TokenPurpose;
    /**
     * The account.
     */
    userId: number;
};

/**
 * Which activation email applies to each account that has not been activated. Accounts that are already active do not appear.
 */
export type PendingActivationsResponse = {
    activations: Array<PendingActivation>;
};

/**
 * A Discord role the bot notifies when it posts the event
 */
export type PingedRoleRequest = {
    id: string;
    /**
     * The role's name as last known, kept for when Discord cannot be asked
     */
    name: string;
};

/**
 * A Discord role the bot notifies when it posts the event
 */
export type PingedRoleResponse = {
    id: string;
    /**
     * The role's name as last known
     */
    name: string;
};

export enum PlatformType {
    FACEBOOK = 'FACEBOOK',
    LINKEDIN = 'LINKEDIN',
    TWITTER = 'TWITTER',
    INSTAGRAM = 'INSTAGRAM'
}

export type PlayedRosterResponse = {
    game: string;
    role: TeamRole;
    roleTitle?: string | null;
    seasonId: number;
    seasonName: string;
    seasonStart: string;
    teamId: number;
    teamName: string;
};

/**
 * A team's whole line-up for a game and a season, saved in one transaction
 */
export type PublishLineupRequest = {
    /**
     * Where this season's banner is stored; nothing leaves the banner as it is
     */
    banner?: string | null;
    /**
     * Everybody on the line-up, in the order they are shown
     */
    entries: Array<LineupEntryRequest>;
    game: string;
    /**
     * Where the team's icon is stored
     */
    icon?: string | null;
    name: string;
    /**
     * Entries taken off the line-up
     */
    removed: Array<number>;
    /**
     * The team; nothing creates it under the name given
     */
    teamId?: number | null;
};

/**
 * A line-up as saved: the team as it now stands and its roster in order
 */
export type PublishedLineupResponse = {
    roster: Array<RosterEntryResponse>;
    team: TeamResponse;
};

export type QuestionRequest = {
    choiceLabels?: Array<string> | null;
    idx: number;
    label: string;
    required?: boolean | null;
    type: QuestionType;
};

export type QuestionResponse = {
    choiceLabels?: Array<string> | null;
    createdAt: string;
    id: number;
    idx: number;
    label: string;
    required?: boolean | null;
    surveyId: number;
    type: QuestionType;
    updatedAt: string;
    version: number;
};

export enum QuestionType {
    OPEN = 'OPEN',
    RADIO = 'RADIO',
    CHECKBOX = 'CHECKBOX',
    DESCRIPTION = 'DESCRIPTION'
}

export type ReasonRequest = {
    reason: string;
};

/**
 * The top rate every sender reached together, SiteCie included, and when
 */
export type RecordResponse = {
    at: string;
    /**
     * The combined rate, in pings per second
     */
    pps: number;
};

/**
 * A recovery email rendered for inspection. No token was issued to produce it.
 */
export type RecoveryEmailPreviewResponse = {
    /**
     * The rendered email, as delivered but for the inert recovery link.
     */
    html: string;
    /**
     * What the recovery link carries in place of a token, so the preview can say the link is inert.
     */
    linkPlaceholder: string;
    /**
     * Which recovery email this is.
     */
    purpose: TokenPurpose;
    /**
     * Address the email would be sent to.
     */
    recipientEmail: string;
    /**
     * Name the email addresses the recipient by.
     */
    recipientName: string;
    /**
     * Subject line the recipient would see.
     */
    subject: string;
};

export type RedirectResponse = {
    path: string;
};

export type ReenrolRequest = {
    password: string;
    token: string;
    username: string;
};

export enum Role {
    ANONYMOUS = 'ANONYMOUS',
    VEGAN = 'VEGAN',
    GUEST = 'GUEST',
    COMPANY = 'COMPANY',
    MEMBER = 'MEMBER',
    COMMITTEE = 'COMMITTEE',
    BOARD = 'BOARD',
    TREASURER = 'TREASURER',
    ADMIN = 'ADMIN',
    SYSTEM = 'SYSTEM'
}

export type RoleChangeResponse = {
    actorId: number;
    actorName: string;
    after: Array<Role>;
    before: Array<Role>;
    changedAt: string;
    id: number;
    note?: string | null;
};

export enum RoleSource {
    GRANT = 'GRANT',
    ACCOUNT = 'ACCOUNT',
    MEMBERSHIP = 'MEMBERSHIP',
    COMMITTEE_SEAT = 'COMMITTEE_SEAT'
}

/**
 * A roster entry as an admin edits it, real name included
 */
export type RosterEntryResponse = {
    /**
     * A short caption about them, in markdown
     */
    description?: string | null;
    /**
     * The member's real name, shown to admins for identification
     */
    displayName?: string | null;
    handle: string;
    /**
     * This entry's uploaded picture, where one was uploaded
     */
    icon?: Image | null;
    id: number;
    role: TeamRole;
    /**
     * What they did in the team's own words
     */
    roleTitle?: string | null;
    seasonId: number;
    sortIndex: number;
    teamId: number;
    /**
     * The member this entry belongs to, when anybody could be identified
     */
    userId?: number | null;
};

/**
 * One person on a team's roster, as the public read has them
 */
export type RosterMemberResponse = {
    /**
     * A short caption about them, in markdown, where anything was written
     */
    description?: string | null;
    /**
     * What this member is called in the game
     */
    handle: string;
    /**
     * This entry's uploaded picture, where one was uploaded
     */
    icon?: Image | null;
    /**
     * The member's real name, present only when they allow it to be shown
     */
    name?: string | null;
    role: TeamRole;
    /**
     * What they did in the team's own words, where anything was said
     */
    roleTitle?: string | null;
};

/**
 * One person on an event's roster
 */
export type RosterPersonResponse = {
    /**
     * Their Discord picture; absent where Discord is not linked
     */
    avatar?: string | null;
    /**
     * Whether the name is their Discord name
     */
    discord: boolean;
    /**
     * Their Discord name where Discord is linked, otherwise their username
     */
    name: string;
};

/**
 * What a season holds, for a removal to say before it happens
 */
export type SeasonContentsResponse = {
    /**
     * Roster places held in it, across those teams
     */
    players: number;
    /**
     * Teams fielded in it, across every game
     */
    teams: number;
};

/**
 * A game that ran in a season, with what it fielded
 */
export type SeasonGameResponse = {
    game: string;
    /**
     * Whether a visitor sees it: a game is public in a season once a team plays it
     */
    public: boolean;
    teams: Array<TeamRosterResponse>;
};

/**
 * Create or rename a season
 */
export type SeasonRequest = {
    endDate: string;
    name: string;
    startDate: string;
};

/**
 * A stretch of play that rosters belong to
 */
export type SeasonResponse = {
    endDate: string;
    id: number;
    name: string;
    /**
     * Whether anything was fielded in it, which is which seasons a visitor is offered
     */
    played: boolean;
    startDate: string;
};

export enum SecurityActorKind {
    PERSON = 'PERSON',
    SYSTEM = 'SYSTEM',
    OPERATOR = 'OPERATOR'
}

export enum SecurityEventKind {
    SIGNED_IN = 'SIGNED_IN',
    NEW_BROWSER = 'NEW_BROWSER',
    SIGN_IN_REUSED = 'SIGN_IN_REUSED',
    SIGN_IN_BROWSER_CHANGED = 'SIGN_IN_BROWSER_CHANGED',
    CODE_LIMIT_REACHED = 'CODE_LIMIT_REACHED',
    PASSWORD_RESET = 'PASSWORD_RESET',
    PASSWORD_CHANGED = 'PASSWORD_CHANGED',
    EMAIL_CHANGE_REQUESTED = 'EMAIL_CHANGE_REQUESTED',
    EMAIL_CHANGED = 'EMAIL_CHANGED',
    EMAIL_CHANGED_BY_BOARD = 'EMAIL_CHANGED_BY_BOARD',
    TWO_FACTOR_ON = 'TWO_FACTOR_ON',
    TWO_FACTOR_OFF = 'TWO_FACTOR_OFF',
    TWO_FACTOR_REPLACED = 'TWO_FACTOR_REPLACED',
    BACKUP_CODES_REGENERATED = 'BACKUP_CODES_REGENERATED',
    BACKUP_CODE_USED = 'BACKUP_CODE_USED',
    TRUSTED_BROWSER_ADDED = 'TRUSTED_BROWSER_ADDED',
    TWO_FACTOR_RESET = 'TWO_FACTOR_RESET',
    REENROLLED = 'REENROLLED',
    ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
    BREAK_GLASS = 'BREAK_GLASS',
    ACCOUNT_UNLOCKED = 'ACCOUNT_UNLOCKED',
    SIGNED_OUT_EVERYWHERE = 'SIGNED_OUT_EVERYWHERE',
    SIGNED_OUT_ELSEWHERE = 'SIGNED_OUT_ELSEWHERE',
    ROLES_CHANGED = 'ROLES_CHANGED'
}

export type SecurityEventPageResponse = {
    events: Array<SecurityEventResponse>;
    page: number;
    totalElements: number;
    totalPages: number;
};

export type SecurityEventResponse = {
    actorKind: SecurityActorKind;
    actorName?: string | null;
    browser?: string | null;
    id: number;
    kind: SecurityEventKind;
    note?: string | null;
    occurredAt: string;
    platform?: string | null;
};

export type SendPaymentEmailsRequest = {
    contributionPeriodId: number | null;
    /**
     * Required exactly when somebody here is getting a pre-notification.
     */
    debitDate?: string | null;
    /**
     * Fee type per member, where the treasurer changed it.
     */
    feeTypeOverrides: {
        [key: string]: BulkFeeType;
    };
    /**
     * Warned members this request overrules. Every one of them must be in the selection and must be somebody the send writes to.
     */
    forciblyIncludedUserIds: Array<number>;
    /**
     * Members moved off the email their direct-debit flag chose.
     */
    kindOverrides: {
        [key: string]: ContributionEmailKind;
    };
    /**
     * Required exactly when somebody here is getting a payment request.
     */
    paymentDueDate?: string | null;
    userIds: Array<number>;
};

export type SentEmailPreview = {
    /**
     * The email's html with every url stripped out
     */
    html: string;
    /**
     * Always true: the preview's links were removed before it left the api
     */
    linksRedacted: boolean;
    recipientEmail: string;
    recipientName: string;
    subject: string;
};

export type ServiceEntry = {
    description: string;
    iconUrl: string;
    id: string;
    name: string;
    url: string;
};

export type SignInAnswer = {
    login?: LoginResponse | null;
    status: SignInStatus;
};

export type SignInResponse = {
    browser: string;
    current: boolean;
    id: string;
    lastSeenAt: string;
    platform: string;
    signedInAt: string;
};

export enum SignInStatus {
    SIGNED_IN = 'SIGNED_IN',
    TWO_FACTOR_REQUIRED = 'TWO_FACTOR_REQUIRED'
}

export type SignupAddressRequest = {
    city: string;
    country: string;
    houseNumber: string;
    street: string;
    zipCode: string;
};

export type SignupApplicationRequest = {
    conditionsAccepted: boolean;
};

export type SignupDetailsRequest = {
    discord: string;
    /**
     * The Discord user ID of the member picked from the server, where one is picked
     */
    discordId?: string | null;
    firstName: string;
    initials: string;
    lastName: string;
    memberProfile?: UpsertMemberProfileRequest | null;
    newsletter: boolean;
    phoneNumber: string;
    photoConsent?: boolean | null;
    prefix?: string | null;
    username: string;
};

export type SignupEmailRequest = {
    email: string;
};

export type SignupOutcomeResponse = {
    emailConfirmed: boolean;
    membershipStarted: boolean;
};

export type SignupResumeAddressResponse = {
    city?: string | null;
    country?: string | null;
    houseNumber?: string | null;
    street?: string | null;
    zipCode?: string | null;
};

export type SignupResumeProfileResponse = {
    bhv: boolean;
    dateOfBirth?: string | null;
    ehbo: boolean;
    gender?: string | null;
    nameOnRosters: boolean;
    nationality?: string | null;
    studentNumber?: string | null;
};

export type SignupResumeResponse = {
    address?: SignupResumeAddressResponse | null;
    /**
     * Whether the membership conditions were already agreed to, which is not retractable
     */
    conditionsAccepted: boolean;
    discord?: string | null;
    /**
     * The linked Discord member's user ID, where one is picked
     */
    discordId?: string | null;
    email: string;
    /**
     * Whether the confirmation link has been opened, which is what enables the account
     */
    emailConfirmed: boolean;
    firstName: string;
    initials: string;
    lastName: string;
    memberProfile?: SignupResumeProfileResponse | null;
    newsletter: boolean;
    phoneNumber?: string | null;
    photoConsent: boolean;
    prefix?: string | null;
    userId: number;
    username: string;
};

export type SignupSessionResponse = {
    email: string;
    expiresAt: string;
    signupToken: string;
    userId: number;
};

export type SponsorResponse = {
    createdAt: string;
    description: string;
    id: number;
    name: string;
    updatedAt: string;
    version: number;
};

/**
 * One ranked member: their Discord tag and avatar when linked, otherwise their site username
 */
export type StandingResponse = {
    avatarUrl?: string | null;
    discordTag?: string | null;
    memberId: number;
    rank: number;
    totalSent: number;
    username?: string | null;
};

/**
 * A message the Discord server starred
 */
export type StarboardEntryResponse = {
    /**
     * The author's name across Discord
     */
    authorName: string;
    /**
     * The author's name in the server, where they set one
     */
    authorNickname?: string | null;
    /**
     * The author's avatar's address, where they have one
     */
    avatar?: string | null;
    /**
     * The name of the channel the message was written in; absent where that channel is members-only
     */
    channel?: string | null;
    /**
     * The address that opens the message in Discord
     */
    href: string;
    /**
     * The starred message's ID
     */
    id: string;
    /**
     * The picture the message carries, where it has one
     */
    image?: string | null;
    postedAt: string;
    stars: number;
    /**
     * The message in Discord markdown, with mentions and server emoji; absent for a picture alone
     */
    text?: string | null;
};

export type StepUpRequest = {
    code?: string | null;
    password?: string | null;
};

export type SurveyRequest = {
    questions: Array<QuestionRequest>;
};

export type SurveyResponse = {
    createdAt: string;
    id: number;
    questions: Array<QuestionResponse>;
    responseCount: number;
    updatedAt: string;
    version: number;
};

export type SwitchTarget = {
    deletePrevious: boolean;
    externalId: string;
    reconcileNow: boolean;
};

export type TargetDescriptor = {
    kind: CohortKind;
    system: TargetSystem;
};

export enum TargetSystem {
    BREVO = 'BREVO',
    GOOGLE_CALENDAR = 'GOOGLE_CALENDAR'
}

/**
 * A team as a write says it stands. A team is the association's rather than a game's, so it names none
 */
export type TeamRequest = {
    /**
     * Where the team's icon is stored; nothing leaves the team without one
     */
    icon?: string | null;
    name: string;
};

/**
 * A team the association fields in one game
 */
export type TeamResponse = {
    /**
     * The team's own icon, drawn beside the name. The banner it is drawn on belongs to the fielding, not to the team
     */
    icon?: Image | null;
    id: number;
    name: string;
};

export enum TeamRole {
    PLAYER = 'PLAYER',
    SUBSTITUTE = 'SUBSTITUTE',
    COACH = 'COACH'
}

/**
 * A team with the roster it fielded in one season
 */
export type TeamRosterResponse = {
    /**
     * The team's own banner
     */
    banner?: Image | null;
    /**
     * The team's own icon, shown beside the name
     */
    icon?: Image | null;
    id: number;
    members: Array<RosterMemberResponse>;
    name: string;
};

export type TelemetryResponse = {
    createdAt: string;
    id: number;
    platform: PlatformType;
    updatedAt: string;
    url: string;
    version: number;
};

export enum TokenPurpose {
    USER_ACTIVATION = 'USER_ACTIVATION',
    MEMBER_ACTIVATION = 'MEMBER_ACTIVATION',
    PASSWORD_RESET = 'PASSWORD_RESET',
    SIGNUP_CONTINUATION = 'SIGNUP_CONTINUATION',
    ACCOUNT_LOCK = 'ACCOUNT_LOCK',
    EMAIL_CHANGE = 'EMAIL_CHANGE',
    TWO_FACTOR_REENROLMENT = 'TWO_FACTOR_REENROLMENT'
}

export type TokenRequest = {
    token: string;
};

export type TrustedBrowserResponse = {
    browser: string;
    expiresAt: string;
    id: number;
    lastUsedAt?: string | null;
    platform: string;
    trustedAt: string;
};

export type TwoFactorCodeRequest = {
    code: string;
    trustThisBrowser: boolean;
};

export type TwoFactorSetUpRequest = {
    password?: string | null;
};

export type TwoFactorSetupResponse = {
    key: string;
    otpauthUri: string;
};

export type TwoFactorStanding = {
    backupCodesLeft: number;
    mayTurnOff: boolean;
    offered: boolean;
    on: boolean;
    required: boolean;
    since?: string | null;
};

export type UnlockRequest = {
    email?: string | null;
    reason: string;
};

export type UpdateAddressRequest = {
    city: string;
    country: string;
    houseNumber: string;
    street: string;
    version: number;
    zipCode: string;
};

export type UpdateBlogRequest = {
    html: string;
    publishedAt: string;
    title: string;
    version: number;
};

export type UpdateBoardMemberRequest = {
    description?: string | null;
    displayName?: string | null;
    endDate?: string | null;
    /**
     * The name the member was known by, without the quotes around it
     */
    nickname?: string | null;
    /**
     * Where the portrait is stored; blank leaves the member without one
     */
    portrait?: string | null;
    role: string;
    startDate: string;
};

export type UpdateCommitteeRequest = {
    /**
     * Where its stored banner is, or absent for none
     */
    banner?: string | null;
    description: string;
    gameCodes?: Array<string> | null;
    /**
     * Where its stored logo is, or absent for none
     */
    icon?: string | null;
    members: Array<CommitteeMemberRequest>;
    name: string;
    /**
     * The address its page answers to; absent makes one from the name
     */
    slug?: string | null;
    version: number;
};

export type UpdateContributionPeriodRequest = {
    alumniFee: number;
    contactListId?: number | null;
    endDate: string;
    fullYearFee: number;
    /**
     * A regular membership starting after this date pays the half-year fee.
     */
    halfYearCutoffDate: string;
    halfYearFee: number;
    startDate: string;
    version: number;
};

export type UpdateEventRequest = {
    /**
     * When the events-info post goes out, which approving must say while it is not out yet
     */
    announce?: AnnounceChoice | null;
    approved: boolean;
    banner?: EventBannerRequest | null;
    committeeId: number;
    description: string;
    endTime: string;
    gameCodes?: Array<string> | null;
    location?: string | null;
    memberPrice?: number | null;
    membersOnly: boolean;
    pingedRoles?: Array<PingedRoleRequest> | null;
    publicPrice?: number | null;
    removeExistingSignUps?: boolean | null;
    signUp: boolean;
    signUpDeadline?: string | null;
    signUpForm?: SurveyRequest | null;
    signUpLimit?: number | null;
    startTime: string;
    title: string;
    version: number;
};

export type UpdateEventSignUpRequest = {
    answers?: Array<AnswerRequest> | null;
    guest?: CreateGuestRequest | null;
    userId?: number | null;
    version?: number | null;
};

export type UpdateMemberProfileRequest = {
    bhv: boolean;
    dateOfBirth: string;
    ehbo: boolean;
    gender?: string | null;
    /**
     * Whether this member's real name may appear in a roster
     */
    nameOnRosters: boolean;
    nationality: string;
    studentNumber?: string | null;
    version: number;
};

export type UpdateMembershipRequest = {
    endDate?: string | null;
    incasso?: boolean | null;
    memberType?: MemberType | null;
    startDate: string | null;
    userId: number;
    version: number;
};

/**
 * Edit a roster entry
 */
export type UpdateRosterEntryRequest = {
    /**
     * A short caption about them, in markdown
     */
    description?: string | null;
    displayName?: string | null;
    handle: string;
    /**
     * Where this entry's picture is stored; nothing takes the picture away
     */
    icon?: string | null;
    role: TeamRole;
    /**
     * What they did in the team's own words, beside the fixed part
     */
    roleTitle?: string | null;
    sortIndex: number;
};

export type UpdateSponsorRequest = {
    description: string;
    name: string;
    version: number;
};

export type UpdateUserRequest = {
    discord: string;
    /**
     * The Discord user ID of the member picked from the server, where one is picked
     */
    discordId?: string | null;
    memberProfile?: UpsertMemberProfileRequest | null;
    newsletter: boolean;
    phoneNumber: string;
    photoConsent?: boolean | null;
    version: number;
};

export type UpdateUserRolesRequest = {
    note?: string | null;
    roles: Array<Role>;
};

export type UpsertMemberProfileRequest = {
    bhv: boolean;
    /**
     * Required of a member; anybody else may leave it out
     */
    dateOfBirth?: string | null;
    ehbo: boolean;
    gender?: string | null;
    /**
     * Whether this member's real name may appear in a roster
     */
    nameOnRosters: boolean;
    /**
     * Required of a member; anybody else may leave it out
     */
    nationality?: string | null;
    studentNumber?: string | null;
    version?: number | null;
};

export type UserActivationRequest = {
    token: string;
};

export type UserDetailResponse = {
    addressId?: number | null;
    awaitingReenrolment: boolean;
    createdAt: string;
    discord?: string | null;
    /**
     * The linked Discord member's user ID, where one is picked
     */
    discordId?: string | null;
    email: string;
    enabled: boolean;
    firstName: string;
    fullName: string;
    id: number;
    initials: string;
    lastName: string;
    locked: boolean;
    nameOnRosters: boolean;
    newsletter: boolean;
    phoneNumber?: string | null;
    photoConsent: boolean;
    prefix?: string | null;
    restoreUntilAt?: string | null;
    roles: Array<Role>;
    twoFactorOn: boolean;
    updatedAt: string;
    username: string;
    version: number;
};

export type UserRolesResponse = {
    assignable: Array<Role>;
    derived: Array<DerivedRoleResponse>;
    dormant: Array<Role>;
    granted: Array<Role>;
    implied: Array<Role>;
    roles: Array<Role>;
    userId: number;
};

export type UserSummaryResponse = {
    createdAt: string;
    discord?: string | null;
    /**
     * The linked Discord member's user ID, where one is picked
     */
    discordId?: string | null;
    email: string;
    fullName: string;
    id: number;
    phoneNumber?: string | null;
    updatedAt: string;
    version: number;
};

export type Version = {
    version: string;
};

export type FindAllAddressesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/addresses';
};

export type FindAllAddressesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindAllAddressesError = FindAllAddressesErrors[keyof FindAllAddressesErrors];

export type FindAllAddressesResponses = {
    /**
     * OK
     */
    200: Array<AddressResponse>;
};

export type FindAllAddressesResponse = FindAllAddressesResponses[keyof FindAllAddressesResponses];

export type CreateAddressData = {
    body: CreateAddressRequest;
    path?: never;
    query?: never;
    url: '/addresses';
};

export type CreateAddressErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateAddressError = CreateAddressErrors[keyof CreateAddressErrors];

export type CreateAddressResponses = {
    /**
     * Created
     */
    201: AddressResponse;
};

export type CreateAddressResponse = CreateAddressResponses[keyof CreateAddressResponses];

export type DeleteAddressByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/addresses/{id}';
};

export type DeleteAddressByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteAddressByIdError = DeleteAddressByIdErrors[keyof DeleteAddressByIdErrors];

export type DeleteAddressByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteAddressByIdResponse = DeleteAddressByIdResponses[keyof DeleteAddressByIdResponses];

export type FindAddressByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/addresses/{id}';
};

export type FindAddressByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindAddressByIdError = FindAddressByIdErrors[keyof FindAddressByIdErrors];

export type FindAddressByIdResponses = {
    /**
     * OK
     */
    200: AddressResponse;
};

export type FindAddressByIdResponse = FindAddressByIdResponses[keyof FindAddressByIdResponses];

export type UpdateAddressData = {
    body: UpdateAddressRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/addresses/{id}';
};

export type UpdateAddressErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateAddressError = UpdateAddressErrors[keyof UpdateAddressErrors];

export type UpdateAddressResponses = {
    /**
     * OK
     */
    200: AddressResponse;
};

export type UpdateAddressResponse = UpdateAddressResponses[keyof UpdateAddressResponses];

export type AuthenticateData = {
    body: JwtRequest;
    path?: never;
    query?: never;
    url: '/auth';
};

export type AuthenticateErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type AuthenticateError = AuthenticateErrors[keyof AuthenticateErrors];

export type AuthenticateResponses = {
    /**
     * OK
     */
    200: SignInAnswer;
};

export type AuthenticateResponse = AuthenticateResponses[keyof AuthenticateResponses];

export type LogoutData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/auth/logout';
};

export type LogoutErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type LogoutError = LogoutErrors[keyof LogoutErrors];

export type LogoutResponses = {
    /**
     * No Content
     */
    204: void;
};

export type LogoutResponse = LogoutResponses[keyof LogoutResponses];

export type StepUpData = {
    body: StepUpRequest;
    path?: never;
    query?: never;
    url: '/auth/step-up';
};

export type StepUpErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type StepUpError = StepUpErrors[keyof StepUpErrors];

export type StepUpResponses = {
    /**
     * No Content
     */
    204: void;
};

export type StepUpResponse = StepUpResponses[keyof StepUpResponses];

export type AnswerChallengeData = {
    body: TwoFactorCodeRequest;
    path?: never;
    query?: never;
    url: '/auth/two-factor';
};

export type AnswerChallengeErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type AnswerChallengeError = AnswerChallengeErrors[keyof AnswerChallengeErrors];

export type AnswerChallengeResponses = {
    /**
     * OK
     */
    200: SignInAnswer;
};

export type AnswerChallengeResponse = AnswerChallengeResponses[keyof AnswerChallengeResponses];

export type FindBlogsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/blogs';
};

export type FindBlogsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindBlogsError = FindBlogsErrors[keyof FindBlogsErrors];

export type FindBlogsResponses = {
    /**
     * OK
     */
    200: Array<BlogResponse>;
};

export type FindBlogsResponse = FindBlogsResponses[keyof FindBlogsResponses];

export type CreateBlogData = {
    body: CreateBlogRequest;
    path?: never;
    query?: never;
    url: '/blogs';
};

export type CreateBlogErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateBlogError = CreateBlogErrors[keyof CreateBlogErrors];

export type CreateBlogResponses = {
    /**
     * Created
     */
    201: BlogResponse;
};

export type CreateBlogResponse = CreateBlogResponses[keyof CreateBlogResponses];

export type DeleteByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/blogs/{id}';
};

export type DeleteByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteByIdError = DeleteByIdErrors[keyof DeleteByIdErrors];

export type DeleteByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteByIdResponse = DeleteByIdResponses[keyof DeleteByIdResponses];

export type FindBlogByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/blogs/{id}';
};

export type FindBlogByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindBlogByIdError = FindBlogByIdErrors[keyof FindBlogByIdErrors];

export type FindBlogByIdResponses = {
    /**
     * OK
     */
    200: BlogResponse;
};

export type FindBlogByIdResponse = FindBlogByIdResponses[keyof FindBlogByIdResponses];

export type UpdateBlogData = {
    body: UpdateBlogRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/blogs/{id}';
};

export type UpdateBlogErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateBlogError = UpdateBlogErrors[keyof UpdateBlogErrors];

export type UpdateBlogResponses = {
    /**
     * OK
     */
    200: BlogResponse;
};

export type UpdateBlogResponse = UpdateBlogResponses[keyof UpdateBlogResponses];

export type FindAllBoardsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/boards';
};

export type FindAllBoardsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindAllBoardsError = FindAllBoardsErrors[keyof FindAllBoardsErrors];

export type FindAllBoardsResponses = {
    /**
     * OK
     */
    200: Array<BoardResponse>;
};

export type FindAllBoardsResponse = FindAllBoardsResponses[keyof FindAllBoardsResponses];

export type CreateBoardData = {
    body: BoardRequest;
    path?: never;
    query?: never;
    url: '/boards';
};

export type CreateBoardErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateBoardError = CreateBoardErrors[keyof CreateBoardErrors];

export type CreateBoardResponses = {
    /**
     * Created
     */
    201: BoardResponse;
};

export type CreateBoardResponse = CreateBoardResponses[keyof CreateBoardResponses];

export type AddMemberData = {
    body: AddBoardMemberRequest;
    path: {
        boardId: number;
    };
    query?: never;
    url: '/boards/{boardId}/members';
};

export type AddMemberErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type AddMemberError = AddMemberErrors[keyof AddMemberErrors];

export type AddMemberResponses = {
    /**
     * Created
     */
    201: BoardMemberResponse;
};

export type AddMemberResponse = AddMemberResponses[keyof AddMemberResponses];

export type RemoveMemberData = {
    body?: never;
    path: {
        boardId: number;
        id: number;
    };
    query?: never;
    url: '/boards/{boardId}/members/{id}';
};

export type RemoveMemberErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RemoveMemberError = RemoveMemberErrors[keyof RemoveMemberErrors];

export type RemoveMemberResponses = {
    /**
     * No Content
     */
    204: void;
};

export type RemoveMemberResponse = RemoveMemberResponses[keyof RemoveMemberResponses];

export type UpdateMemberData = {
    body: UpdateBoardMemberRequest;
    path: {
        boardId: number;
        id: number;
    };
    query?: never;
    url: '/boards/{boardId}/members/{id}';
};

export type UpdateMemberErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateMemberError = UpdateMemberErrors[keyof UpdateMemberErrors];

export type UpdateMemberResponses = {
    /**
     * OK
     */
    200: BoardMemberResponse;
};

export type UpdateMemberResponse = UpdateMemberResponses[keyof UpdateMemberResponses];

export type LinkMemberData = {
    body: LinkBoardMemberRequest;
    path: {
        boardId: number;
        id: number;
    };
    query?: never;
    url: '/boards/{boardId}/members/{id}/member';
};

export type LinkMemberErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type LinkMemberError = LinkMemberErrors[keyof LinkMemberErrors];

export type LinkMemberResponses = {
    /**
     * OK
     */
    200: BoardMemberResponse;
};

export type LinkMemberResponse = LinkMemberResponses[keyof LinkMemberResponses];

export type DeleteBoardData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/boards/{id}';
};

export type DeleteBoardErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteBoardError = DeleteBoardErrors[keyof DeleteBoardErrors];

export type DeleteBoardResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteBoardResponse = DeleteBoardResponses[keyof DeleteBoardResponses];

export type FindBoardByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/boards/{id}';
};

export type FindBoardByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindBoardByIdError = FindBoardByIdErrors[keyof FindBoardByIdErrors];

export type FindBoardByIdResponses = {
    /**
     * OK
     */
    200: BoardResponse;
};

export type FindBoardByIdResponse = FindBoardByIdResponses[keyof FindBoardByIdResponses];

export type UpdateBoardData = {
    body: BoardRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/boards/{id}';
};

export type UpdateBoardErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateBoardError = UpdateBoardErrors[keyof UpdateBoardErrors];

export type UpdateBoardResponses = {
    /**
     * OK
     */
    200: BoardResponse;
};

export type UpdateBoardResponse = UpdateBoardResponses[keyof UpdateBoardResponses];

export type FindCommitteesByUserIdData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/committeeMembers/committees';
};

export type FindCommitteesByUserIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCommitteesByUserIdError = FindCommitteesByUserIdErrors[keyof FindCommitteesByUserIdErrors];

export type FindCommitteesByUserIdResponses = {
    /**
     * OK
     */
    200: Array<CommitteeResponse>;
};

export type FindCommitteesByUserIdResponse = FindCommitteesByUserIdResponses[keyof FindCommitteesByUserIdResponses];

export type FindCommitteesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/committees';
};

export type FindCommitteesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCommitteesError = FindCommitteesErrors[keyof FindCommitteesErrors];

export type FindCommitteesResponses = {
    /**
     * OK
     */
    200: Array<CommitteeResponse>;
};

export type FindCommitteesResponse = FindCommitteesResponses[keyof FindCommitteesResponses];

export type CreateCommitteeData = {
    body: CreateCommitteeRequest;
    path?: never;
    query?: never;
    url: '/committees';
};

export type CreateCommitteeErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateCommitteeError = CreateCommitteeErrors[keyof CreateCommitteeErrors];

export type CreateCommitteeResponses = {
    /**
     * Created
     */
    201: CommitteeResponse;
};

export type CreateCommitteeResponse = CreateCommitteeResponses[keyof CreateCommitteeResponses];

export type FindCommitteePageData = {
    body?: never;
    path: {
        address: string;
    };
    query?: never;
    url: '/committees/address/{address}';
};

export type FindCommitteePageErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCommitteePageError = FindCommitteePageErrors[keyof FindCommitteePageErrors];

export type FindCommitteePageResponses = {
    /**
     * OK
     */
    200: CommitteePageResponse;
};

export type FindCommitteePageResponse = FindCommitteePageResponses[keyof FindCommitteePageResponses];

export type SetGameOrganisersData = {
    body: GameOrganisersRequest;
    path: {
        game: string;
    };
    query?: never;
    url: '/committees/games/{game}';
};

export type SetGameOrganisersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SetGameOrganisersError = SetGameOrganisersErrors[keyof SetGameOrganisersErrors];

export type SetGameOrganisersResponses = {
    /**
     * OK
     */
    200: Array<CommitteeResponse>;
};

export type SetGameOrganisersResponse = SetGameOrganisersResponses[keyof SetGameOrganisersResponses];

export type FindCommitteeByIdData = {
    body?: never;
    path: {
        committeeId: number;
    };
    query?: never;
    url: '/committees/{committeeId}';
};

export type FindCommitteeByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCommitteeByIdError = FindCommitteeByIdErrors[keyof FindCommitteeByIdErrors];

export type FindCommitteeByIdResponses = {
    /**
     * OK
     */
    200: CommitteeResponse;
};

export type FindCommitteeByIdResponse = FindCommitteeByIdResponses[keyof FindCommitteeByIdResponses];

export type DeleteCommitteeByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: {
        takenOverBy?: number;
    };
    url: '/committees/{id}';
};

export type DeleteCommitteeByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteCommitteeByIdError = DeleteCommitteeByIdErrors[keyof DeleteCommitteeByIdErrors];

export type DeleteCommitteeByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteCommitteeByIdResponse = DeleteCommitteeByIdResponses[keyof DeleteCommitteeByIdResponses];

export type UpdateCommitteeData = {
    body: UpdateCommitteeRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/committees/{id}';
};

export type UpdateCommitteeErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateCommitteeError = UpdateCommitteeErrors[keyof UpdateCommitteeErrors];

export type UpdateCommitteeResponses = {
    /**
     * OK
     */
    200: CommitteeResponse;
};

export type UpdateCommitteeResponse = UpdateCommitteeResponses[keyof UpdateCommitteeResponses];

export type ArchiveCommitteeData = {
    body: ArchiveCommitteeRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/committees/{id}/archived';
};

export type ArchiveCommitteeErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ArchiveCommitteeError = ArchiveCommitteeErrors[keyof ArchiveCommitteeErrors];

export type ArchiveCommitteeResponses = {
    /**
     * OK
     */
    200: CommitteeResponse;
};

export type ArchiveCommitteeResponse = ArchiveCommitteeResponses[keyof ArchiveCommitteeResponses];

export type UploadCommitteeBannerData = {
    body?: {
        file: Blob | File;
    };
    path: {
        id: number;
    };
    query?: never;
    url: '/committees/{id}/banners';
};

export type UploadCommitteeBannerErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UploadCommitteeBannerError = UploadCommitteeBannerErrors[keyof UploadCommitteeBannerErrors];

export type UploadCommitteeBannerResponses = {
    /**
     * Created
     */
    201: Image;
};

export type UploadCommitteeBannerResponse = UploadCommitteeBannerResponses[keyof UploadCommitteeBannerResponses];

export type FindCommitteeDeletionData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/committees/{id}/deletion';
};

export type FindCommitteeDeletionErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCommitteeDeletionError = FindCommitteeDeletionErrors[keyof FindCommitteeDeletionErrors];

export type FindCommitteeDeletionResponses = {
    /**
     * OK
     */
    200: CommitteeDeletionResponse;
};

export type FindCommitteeDeletionResponse = FindCommitteeDeletionResponses[keyof FindCommitteeDeletionResponses];

export type UploadCommitteeIconData = {
    body?: {
        file: Blob | File;
    };
    path: {
        id: number;
    };
    query?: never;
    url: '/committees/{id}/icons';
};

export type UploadCommitteeIconErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UploadCommitteeIconError = UploadCommitteeIconErrors[keyof UploadCommitteeIconErrors];

export type UploadCommitteeIconResponses = {
    /**
     * Created
     */
    201: Image;
};

export type UploadCommitteeIconResponse = UploadCommitteeIconResponses[keyof UploadCommitteeIconResponses];

export type UpdateCommitteePageData = {
    body: CommitteeOwnPageRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/committees/{id}/page';
};

export type UpdateCommitteePageErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateCommitteePageError = UpdateCommitteePageErrors[keyof UpdateCommitteePageErrors];

export type UpdateCommitteePageResponses = {
    /**
     * OK
     */
    200: CommitteeResponse;
};

export type UpdateCommitteePageResponse = UpdateCommitteePageResponses[keyof UpdateCommitteePageResponses];

export type FindContributionPeriodsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/contributionPeriods';
};

export type FindContributionPeriodsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindContributionPeriodsError = FindContributionPeriodsErrors[keyof FindContributionPeriodsErrors];

export type FindContributionPeriodsResponses = {
    /**
     * OK
     */
    200: Array<ContributionPeriodResponse>;
};

export type FindContributionPeriodsResponse = FindContributionPeriodsResponses[keyof FindContributionPeriodsResponses];

export type CreateContributionPeriodData = {
    body: CreateContributionPeriodRequest;
    path?: never;
    query?: never;
    url: '/contributionPeriods';
};

export type CreateContributionPeriodErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateContributionPeriodError = CreateContributionPeriodErrors[keyof CreateContributionPeriodErrors];

export type CreateContributionPeriodResponses = {
    /**
     * Created
     */
    201: ContributionPeriodResponse;
};

export type CreateContributionPeriodResponse = CreateContributionPeriodResponses[keyof CreateContributionPeriodResponses];

export type FindCurrentContributionPeriodData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/contributionPeriods/current';
};

export type FindCurrentContributionPeriodErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCurrentContributionPeriodError = FindCurrentContributionPeriodErrors[keyof FindCurrentContributionPeriodErrors];

export type FindCurrentContributionPeriodResponses = {
    /**
     * OK
     */
    200: ContributionPeriodResponse;
};

export type FindCurrentContributionPeriodResponse = FindCurrentContributionPeriodResponses[keyof FindCurrentContributionPeriodResponses];

export type DeleteContributionData = {
    body?: never;
    path: {
        userId: number;
        contributionPeriodId: number;
    };
    query?: never;
    url: '/contributionPeriods/{contributionPeriodId}/users/{userId}/contributions';
};

export type DeleteContributionErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteContributionError = DeleteContributionErrors[keyof DeleteContributionErrors];

export type DeleteContributionResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteContributionResponse = DeleteContributionResponses[keyof DeleteContributionResponses];

export type DeleteContributionPeriodByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/contributionPeriods/{id}';
};

export type DeleteContributionPeriodByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteContributionPeriodByIdError = DeleteContributionPeriodByIdErrors[keyof DeleteContributionPeriodByIdErrors];

export type DeleteContributionPeriodByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteContributionPeriodByIdResponse = DeleteContributionPeriodByIdResponses[keyof DeleteContributionPeriodByIdResponses];

export type UpdateContributionPeriodData = {
    body: UpdateContributionPeriodRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/contributionPeriods/{id}';
};

export type UpdateContributionPeriodErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateContributionPeriodError = UpdateContributionPeriodErrors[keyof UpdateContributionPeriodErrors];

export type UpdateContributionPeriodResponses = {
    /**
     * OK
     */
    200: ContributionPeriodResponse;
};

export type UpdateContributionPeriodResponse = UpdateContributionPeriodResponses[keyof UpdateContributionPeriodResponses];

export type FindContributionsByPeriodIdData = {
    body?: never;
    path: {
        periodId: number;
    };
    query?: never;
    url: '/contributionPeriods/{periodId}/contributions';
};

export type FindContributionsByPeriodIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindContributionsByPeriodIdError = FindContributionsByPeriodIdErrors[keyof FindContributionsByPeriodIdErrors];

export type FindContributionsByPeriodIdResponses = {
    /**
     * OK
     */
    200: Array<ContributionResponse>;
};

export type FindContributionsByPeriodIdResponse = FindContributionsByPeriodIdResponses[keyof FindContributionsByPeriodIdResponses];

export type FindContributionRemindersData = {
    body?: never;
    path?: never;
    query: {
        contributionPeriodId: number;
    };
    url: '/contributionReminders';
};

export type FindContributionRemindersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindContributionRemindersError = FindContributionRemindersErrors[keyof FindContributionRemindersErrors];

export type FindContributionRemindersResponses = {
    /**
     * OK
     */
    200: Array<ContributionReminderResponse>;
};

export type FindContributionRemindersResponse = FindContributionRemindersResponses[keyof FindContributionRemindersResponses];

export type SendContributionReminderData = {
    body: CreateContributionReminderRequest;
    path?: never;
    query?: never;
    url: '/contributionReminders';
};

export type SendContributionReminderErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SendContributionReminderError = SendContributionReminderErrors[keyof SendContributionReminderErrors];

export type SendContributionReminderResponses = {
    /**
     * Created
     */
    201: ContributionReminderResponse;
};

export type SendContributionReminderResponse = SendContributionReminderResponses[keyof SendContributionReminderResponses];

export type SendContributionReminderBatchData = {
    body: Array<CreateContributionReminderRequest>;
    path?: never;
    query?: never;
    url: '/contributionReminders/batch';
};

export type SendContributionReminderBatchErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SendContributionReminderBatchError = SendContributionReminderBatchErrors[keyof SendContributionReminderBatchErrors];

export type SendContributionReminderBatchResponses = {
    /**
     * Created
     */
    201: Array<ContributionReminderResponse>;
};

export type SendContributionReminderBatchResponse = SendContributionReminderBatchResponses[keyof SendContributionReminderBatchResponses];

export type FindContributionsData = {
    body?: never;
    path?: never;
    query: {
        contributionPeriodId: number;
    };
    url: '/contributions';
};

export type FindContributionsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindContributionsError = FindContributionsErrors[keyof FindContributionsErrors];

export type FindContributionsResponses = {
    /**
     * OK
     */
    200: Array<ContributionResponse>;
};

export type FindContributionsResponse = FindContributionsResponses[keyof FindContributionsResponses];

export type CreateContributionData = {
    body: CreateContributionRequest;
    path?: never;
    query?: never;
    url: '/contributions';
};

export type CreateContributionErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateContributionError = CreateContributionErrors[keyof CreateContributionErrors];

export type CreateContributionResponses = {
    /**
     * Created
     */
    201: ContributionResponse;
};

export type CreateContributionResponse = CreateContributionResponses[keyof CreateContributionResponses];

export type FindBankAccountData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/contributions/bank-account';
};

export type FindBankAccountErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindBankAccountError = FindBankAccountErrors[keyof FindBankAccountErrors];

export type FindBankAccountResponses = {
    /**
     * OK
     */
    200: BankAccountResponse;
};

export type FindBankAccountResponse = FindBankAccountResponses[keyof FindBankAccountResponses];

export type ReadContributionEmailData = {
    body?: never;
    path?: never;
    query: {
        kind: ContributionEmailKind;
        contributionPeriodId: number;
        userId: number;
        date: string;
        feeType?: BulkFeeType;
    };
    url: '/contributions/bulk/email/message';
};

export type ReadContributionEmailErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ReadContributionEmailError = ReadContributionEmailErrors[keyof ReadContributionEmailErrors];

export type ReadContributionEmailResponses = {
    /**
     * OK
     */
    200: ContributionEmailMessageResponse;
};

export type ReadContributionEmailResponse = ReadContributionEmailResponses[keyof ReadContributionEmailResponses];

export type PreviewBulkContributionEmailData = {
    body: BulkContributionEmailPreviewRequest;
    path?: never;
    query?: never;
    url: '/contributions/bulk/email/preview';
};

export type PreviewBulkContributionEmailErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PreviewBulkContributionEmailError = PreviewBulkContributionEmailErrors[keyof PreviewBulkContributionEmailErrors];

export type PreviewBulkContributionEmailResponses = {
    /**
     * OK
     */
    200: BulkContributionEmailPreviewResponse;
};

export type PreviewBulkContributionEmailResponse = PreviewBulkContributionEmailResponses[keyof PreviewBulkContributionEmailResponses];

export type SendPaymentEmailsData = {
    body: SendPaymentEmailsRequest;
    path?: never;
    query?: never;
    url: '/contributions/bulk/email/send';
};

export type SendPaymentEmailsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SendPaymentEmailsError = SendPaymentEmailsErrors[keyof SendPaymentEmailsErrors];

export type SendPaymentEmailsResponses = {
    /**
     * OK
     */
    200: PaymentEmailsResultResponse;
};

export type SendPaymentEmailsResponse = SendPaymentEmailsResponses[keyof SendPaymentEmailsResponses];

export type MarkPaidData = {
    body: BulkMarkPaidRequest;
    path?: never;
    query?: never;
    url: '/contributions/bulk/mark-paid';
};

export type MarkPaidErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type MarkPaidError = MarkPaidErrors[keyof MarkPaidErrors];

export type MarkPaidResponses = {
    /**
     * OK
     */
    200: BulkActionResult;
};

export type MarkPaidResponse = MarkPaidResponses[keyof MarkPaidResponses];

export type MarkUnpaidData = {
    body: BulkMarkUnpaidRequest;
    path?: never;
    query?: never;
    url: '/contributions/bulk/mark-unpaid';
};

export type MarkUnpaidErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type MarkUnpaidError = MarkUnpaidErrors[keyof MarkUnpaidErrors];

export type MarkUnpaidResponses = {
    /**
     * OK
     */
    200: BulkActionResult;
};

export type MarkUnpaidResponse = MarkUnpaidResponses[keyof MarkUnpaidResponses];

export type CsrfData = {
    body?: never;
    path?: never;
    query: {
        csrfToken: CsrfToken;
    };
    url: '/csrf';
};

export type CsrfErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CsrfError = CsrfErrors[keyof CsrfErrors];

export type CsrfResponses = {
    /**
     * OK
     */
    200: {
        [key: string]: string;
    };
};

export type CsrfResponse = CsrfResponses[keyof CsrfResponses];

export type PreviewDescriptionData = {
    body: DescriptionPreviewRequest;
    path?: never;
    query?: never;
    url: '/descriptions/preview';
};

export type PreviewDescriptionErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PreviewDescriptionError = PreviewDescriptionErrors[keyof PreviewDescriptionErrors];

export type PreviewDescriptionResponses = {
    /**
     * OK
     */
    200: DescriptionTree;
};

export type PreviewDescriptionResponse = PreviewDescriptionResponses[keyof PreviewDescriptionResponses];

export type OpenDiscordChannelData = {
    body?: never;
    path: {
        door: 'welcome' | 'board' | 'suggestions' | 'starboard';
    };
    query?: never;
    url: '/discord/channel/{door}';
};

export type OpenDiscordChannelErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * No such door
     */
    404: unknown;
    /**
     * Server error
     */
    500: ApiError;
};

export type OpenDiscordChannelError = OpenDiscordChannelErrors[keyof OpenDiscordChannelErrors];

export type ListDiscordChannelsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/discord/channels';
};

export type ListDiscordChannelsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or Discord did not answer
     */
    503: unknown;
};

export type ListDiscordChannelsError = ListDiscordChannelsErrors[keyof ListDiscordChannelsErrors];

export type ListDiscordChannelsResponses = {
    /**
     * OK
     */
    200: Array<DiscordMentionChannelResponse>;
};

export type ListDiscordChannelsResponse = ListDiscordChannelsResponses[keyof ListDiscordChannelsResponses];

export type ListDiscordEmojisData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/discord/emojis';
};

export type ListDiscordEmojisErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or Discord did not answer
     */
    503: unknown;
};

export type ListDiscordEmojisError = ListDiscordEmojisErrors[keyof ListDiscordEmojisErrors];

export type ListDiscordEmojisResponses = {
    /**
     * OK
     */
    200: Array<DiscordEmojiResponse>;
};

export type ListDiscordEmojisResponse = ListDiscordEmojisResponses[keyof ListDiscordEmojisResponses];

export type ListGameChannelsData = {
    body?: never;
    path?: never;
    query?: {
        category?: GameChannelCategory;
    };
    url: '/discord/game-channels';
};

export type ListGameChannelsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up
     */
    503: unknown;
};

export type ListGameChannelsError = ListGameChannelsErrors[keyof ListGameChannelsErrors];

export type ListGameChannelsResponses = {
    /**
     * OK
     */
    200: Array<DiscordChannelResponse>;
};

export type ListGameChannelsResponse = ListGameChannelsResponses[keyof ListGameChannelsResponses];

export type OpenDiscordInviteData = {
    body?: never;
    path: {
        door: 'welcome' | 'board' | 'suggestions' | 'starboard';
    };
    query?: never;
    url: '/discord/invite/{door}';
};

export type OpenDiscordInviteErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * No such door
     */
    404: unknown;
    /**
     * Server error
     */
    500: ApiError;
};

export type OpenDiscordInviteError = OpenDiscordInviteErrors[keyof OpenDiscordInviteErrors];

export type ReadDiscordLiveData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/discord/live';
};

export type ReadDiscordLiveErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or not connected yet
     */
    503: unknown;
};

export type ReadDiscordLiveError = ReadDiscordLiveErrors[keyof ReadDiscordLiveErrors];

export type ReadDiscordLiveResponses = {
    /**
     * OK
     */
    200: DiscordLiveResponse;
};

export type ReadDiscordLiveResponse = ReadDiscordLiveResponses[keyof ReadDiscordLiveResponses];

export type ReadMyDiscordRoomsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/discord/live/mine';
};

export type ReadMyDiscordRoomsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or not connected yet
     */
    503: unknown;
};

export type ReadMyDiscordRoomsError = ReadMyDiscordRoomsErrors[keyof ReadMyDiscordRoomsErrors];

export type ReadMyDiscordRoomsResponses = {
    /**
     * OK
     */
    200: DiscordViewerRoomsResponse;
};

export type ReadMyDiscordRoomsResponse = ReadMyDiscordRoomsResponses[keyof ReadMyDiscordRoomsResponses];

export type SearchDiscordMembersData = {
    body?: never;
    path?: never;
    query: {
        query: string;
    };
    url: '/discord/members';
};

export type SearchDiscordMembersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or Discord did not answer
     */
    503: unknown;
};

export type SearchDiscordMembersError = SearchDiscordMembersErrors[keyof SearchDiscordMembersErrors];

export type SearchDiscordMembersResponses = {
    /**
     * OK
     */
    200: Array<DiscordMemberResponse>;
};

export type SearchDiscordMembersResponse = SearchDiscordMembersResponses[keyof SearchDiscordMembersResponses];

export type ListUnclaimedDiscordMembersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/discord/members/unclaimed';
};

export type ListUnclaimedDiscordMembersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or Discord did not answer
     */
    503: unknown;
};

export type ListUnclaimedDiscordMembersError = ListUnclaimedDiscordMembersErrors[keyof ListUnclaimedDiscordMembersErrors];

export type ListUnclaimedDiscordMembersResponses = {
    /**
     * OK
     */
    200: Array<DiscordMemberResponse>;
};

export type ListUnclaimedDiscordMembersResponse = ListUnclaimedDiscordMembersResponses[keyof ListUnclaimedDiscordMembersResponses];

export type ReadDiscordMentionsData = {
    body?: never;
    path?: never;
    query?: {
        users?: Array<string>;
        roles?: Array<string>;
        channels?: Array<string>;
    };
    url: '/discord/mentions';
};

export type ReadDiscordMentionsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or Discord did not answer
     */
    503: unknown;
};

export type ReadDiscordMentionsError = ReadDiscordMentionsErrors[keyof ReadDiscordMentionsErrors];

export type ReadDiscordMentionsResponses = {
    /**
     * OK
     */
    200: DiscordMentionsResponse;
};

export type ReadDiscordMentionsResponse = ReadDiscordMentionsResponses[keyof ReadDiscordMentionsResponses];

export type ListDiscordRolesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/discord/roles';
};

export type ListDiscordRolesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or Discord did not answer
     */
    503: unknown;
};

export type ListDiscordRolesError = ListDiscordRolesErrors[keyof ListDiscordRolesErrors];

export type ListDiscordRolesResponses = {
    /**
     * OK
     */
    200: Array<DiscordRoleResponse>;
};

export type ListDiscordRolesResponse = ListDiscordRolesResponses[keyof ListDiscordRolesResponses];

export type ReadStarboardData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/discord/starboard';
};

export type ReadStarboardErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
    /**
     * The bot is not set up, or Discord has not answered yet
     */
    503: unknown;
};

export type ReadStarboardError = ReadStarboardErrors[keyof ReadStarboardErrors];

export type ReadStarboardResponses = {
    /**
     * OK
     */
    200: Array<StarboardEntryResponse>;
};

export type ReadStarboardResponse = ReadStarboardResponses[keyof ReadStarboardResponses];

export type FindGameData = {
    body?: never;
    path: {
        game: string;
    };
    query?: {
        seasonId?: number;
    };
    url: '/esports/games/{game}';
};

export type FindGameErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindGameError = FindGameErrors[keyof FindGameErrors];

export type FindGameResponses = {
    /**
     * OK
     */
    200: GameRostersResponse;
};

export type FindGameResponse = FindGameResponses[keyof FindGameResponses];

export type RemoveRosterEntryData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/roster/{id}';
};

export type RemoveRosterEntryErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RemoveRosterEntryError = RemoveRosterEntryErrors[keyof RemoveRosterEntryErrors];

export type RemoveRosterEntryResponses = {
    /**
     * No Content
     */
    204: void;
};

export type RemoveRosterEntryResponse = RemoveRosterEntryResponses[keyof RemoveRosterEntryResponses];

export type UpdateRosterEntryData = {
    body: UpdateRosterEntryRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/roster/{id}';
};

export type UpdateRosterEntryErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateRosterEntryError = UpdateRosterEntryErrors[keyof UpdateRosterEntryErrors];

export type UpdateRosterEntryResponses = {
    /**
     * OK
     */
    200: RosterEntryResponse;
};

export type UpdateRosterEntryResponse = UpdateRosterEntryResponses[keyof UpdateRosterEntryResponses];

export type LinkRosterEntryData = {
    body: LinkRosterEntryRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/roster/{id}/member';
};

export type LinkRosterEntryErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type LinkRosterEntryError = LinkRosterEntryErrors[keyof LinkRosterEntryErrors];

export type LinkRosterEntryResponses = {
    /**
     * OK
     */
    200: RosterEntryResponse;
};

export type LinkRosterEntryResponse = LinkRosterEntryResponses[keyof LinkRosterEntryResponses];

export type FindSeasonsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/esports/seasons';
};

export type FindSeasonsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindSeasonsError = FindSeasonsErrors[keyof FindSeasonsErrors];

export type FindSeasonsResponses = {
    /**
     * OK
     */
    200: Array<SeasonResponse>;
};

export type FindSeasonsResponse = FindSeasonsResponses[keyof FindSeasonsResponses];

export type CreateSeasonData = {
    body: SeasonRequest;
    path?: never;
    query?: never;
    url: '/esports/seasons';
};

export type CreateSeasonErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateSeasonError = CreateSeasonErrors[keyof CreateSeasonErrors];

export type CreateSeasonResponses = {
    /**
     * Created
     */
    201: SeasonResponse;
};

export type CreateSeasonResponse = CreateSeasonResponses[keyof CreateSeasonResponses];

export type DeleteSeasonData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/seasons/{id}';
};

export type DeleteSeasonErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteSeasonError = DeleteSeasonErrors[keyof DeleteSeasonErrors];

export type DeleteSeasonResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteSeasonResponse = DeleteSeasonResponses[keyof DeleteSeasonResponses];

export type UpdateSeasonData = {
    body: SeasonRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/seasons/{id}';
};

export type UpdateSeasonErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateSeasonError = UpdateSeasonErrors[keyof UpdateSeasonErrors];

export type UpdateSeasonResponses = {
    /**
     * OK
     */
    200: SeasonResponse;
};

export type UpdateSeasonResponse = UpdateSeasonResponses[keyof UpdateSeasonResponses];

export type FindSeasonContentsData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/seasons/{id}/contents';
};

export type FindSeasonContentsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindSeasonContentsError = FindSeasonContentsErrors[keyof FindSeasonContentsErrors];

export type FindSeasonContentsResponses = {
    /**
     * OK
     */
    200: SeasonContentsResponse;
};

export type FindSeasonContentsResponse = FindSeasonContentsResponses[keyof FindSeasonContentsResponses];

export type FindSeasonGamesData = {
    body?: never;
    path: {
        seasonId: number;
    };
    query?: never;
    url: '/esports/seasons/{seasonId}/games';
};

export type FindSeasonGamesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindSeasonGamesError = FindSeasonGamesErrors[keyof FindSeasonGamesErrors];

export type FindSeasonGamesResponses = {
    /**
     * OK
     */
    200: Array<SeasonGameResponse>;
};

export type FindSeasonGamesResponse = FindSeasonGamesResponses[keyof FindSeasonGamesResponses];

export type LeaveGameData = {
    body?: never;
    path: {
        seasonId: number;
        game: string;
    };
    query?: never;
    url: '/esports/seasons/{seasonId}/games/{game}';
};

export type LeaveGameErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type LeaveGameError = LeaveGameErrors[keyof LeaveGameErrors];

export type LeaveGameResponses = {
    /**
     * No Content
     */
    204: void;
};

export type LeaveGameResponse = LeaveGameResponses[keyof LeaveGameResponses];

export type EnterGameData = {
    body?: never;
    path: {
        seasonId: number;
        game: string;
    };
    query?: never;
    url: '/esports/seasons/{seasonId}/games/{game}';
};

export type EnterGameErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type EnterGameError = EnterGameErrors[keyof EnterGameErrors];

export type EnterGameResponses = {
    /**
     * OK
     */
    200: SeasonGameResponse;
};

export type EnterGameResponse = EnterGameResponses[keyof EnterGameResponses];

export type PublishLineupData = {
    body: PublishLineupRequest;
    path: {
        seasonId: number;
    };
    query?: never;
    url: '/esports/seasons/{seasonId}/lineup';
};

export type PublishLineupErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PublishLineupError = PublishLineupErrors[keyof PublishLineupErrors];

export type PublishLineupResponses = {
    /**
     * OK
     */
    200: PublishedLineupResponse;
};

export type PublishLineupResponse = PublishLineupResponses[keyof PublishLineupResponses];

export type UnfieldTeamData = {
    body?: never;
    path: {
        seasonId: number;
        teamId: number;
    };
    query: {
        game: string;
    };
    url: '/esports/seasons/{seasonId}/teams/{teamId}';
};

export type UnfieldTeamErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UnfieldTeamError = UnfieldTeamErrors[keyof UnfieldTeamErrors];

export type UnfieldTeamResponses = {
    /**
     * No Content
     */
    204: void;
};

export type UnfieldTeamResponse = UnfieldTeamResponses[keyof UnfieldTeamResponses];

export type FieldTeamData = {
    body: FieldTeamRequest;
    path: {
        seasonId: number;
        teamId: number;
    };
    query?: never;
    url: '/esports/seasons/{seasonId}/teams/{teamId}';
};

export type FieldTeamErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FieldTeamError = FieldTeamErrors[keyof FieldTeamErrors];

export type FieldTeamResponses = {
    /**
     * OK
     */
    200: FieldedTeamResponse;
};

export type FieldTeamResponse = FieldTeamResponses[keyof FieldTeamResponses];

export type FindTeamsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/esports/teams';
};

export type FindTeamsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindTeamsError = FindTeamsErrors[keyof FindTeamsErrors];

export type FindTeamsResponses = {
    /**
     * OK
     */
    200: Array<TeamResponse>;
};

export type FindTeamsResponse = FindTeamsResponses[keyof FindTeamsResponses];

export type CreateTeamData = {
    body: TeamRequest;
    path?: never;
    query?: never;
    url: '/esports/teams';
};

export type CreateTeamErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateTeamError = CreateTeamErrors[keyof CreateTeamErrors];

export type CreateTeamResponses = {
    /**
     * Created
     */
    201: TeamResponse;
};

export type CreateTeamResponse = CreateTeamResponses[keyof CreateTeamResponses];

export type DeleteTeamData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/teams/{id}';
};

export type DeleteTeamErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteTeamError = DeleteTeamErrors[keyof DeleteTeamErrors];

export type DeleteTeamResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteTeamResponse = DeleteTeamResponses[keyof DeleteTeamResponses];

export type UpdateTeamData = {
    body: TeamRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/esports/teams/{id}';
};

export type UpdateTeamErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateTeamError = UpdateTeamErrors[keyof UpdateTeamErrors];

export type UpdateTeamResponses = {
    /**
     * OK
     */
    200: TeamResponse;
};

export type UpdateTeamResponse = UpdateTeamResponses[keyof UpdateTeamResponses];

export type FindRosterData = {
    body?: never;
    path: {
        teamId: number;
    };
    query: {
        game: string;
        seasonId: number;
    };
    url: '/esports/teams/{teamId}/roster';
};

export type FindRosterErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindRosterError = FindRosterErrors[keyof FindRosterErrors];

export type FindRosterResponses = {
    /**
     * OK
     */
    200: Array<RosterEntryResponse>;
};

export type FindRosterResponse = FindRosterResponses[keyof FindRosterResponses];

export type AddRosterEntryData = {
    body: AddRosterEntryRequest;
    path: {
        teamId: number;
    };
    query?: never;
    url: '/esports/teams/{teamId}/roster';
};

export type AddRosterEntryErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type AddRosterEntryError = AddRosterEntryErrors[keyof AddRosterEntryErrors];

export type AddRosterEntryResponses = {
    /**
     * Created
     */
    201: RosterEntryResponse;
};

export type AddRosterEntryResponse = AddRosterEntryResponses[keyof AddRosterEntryResponses];

export type FindTeamSeasonsData = {
    body?: never;
    path: {
        teamId: number;
    };
    query?: never;
    url: '/esports/teams/{teamId}/seasons';
};

export type FindTeamSeasonsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindTeamSeasonsError = FindTeamSeasonsErrors[keyof FindTeamSeasonsErrors];

export type FindTeamSeasonsResponses = {
    /**
     * OK
     */
    200: Array<FieldingResponse>;
};

export type FindTeamSeasonsResponse = FindTeamSeasonsResponses[keyof FindTeamSeasonsResponses];

export type FindEventsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Zero-based page index (0..N)
         */
        page?: number;
        /**
         * The size of the page to be returned
         */
        size?: number;
        /**
         * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
         */
        sort?: Array<string>;
        from?: string;
        to?: string;
        approved?: boolean;
        committeeId?: number;
        titleContains?: string;
        hasBanner?: boolean;
        gameCode?: string;
    };
    url: '/events';
};

export type FindEventsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindEventsError = FindEventsErrors[keyof FindEventsErrors];

export type FindEventsResponses = {
    /**
     * OK
     */
    200: PagedModelEventResponse;
};

export type FindEventsResponse = FindEventsResponses[keyof FindEventsResponses];

export type CreateEventData = {
    body: CreateEventRequest;
    path?: never;
    query?: never;
    url: '/events';
};

export type CreateEventErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateEventError = CreateEventErrors[keyof CreateEventErrors];

export type CreateEventResponses = {
    /**
     * Created
     */
    201: EventResponse;
};

export type CreateEventResponse = CreateEventResponses[keyof CreateEventResponses];

export type UploadEventBannerData = {
    body: {
        file: Blob | File;
    };
    path?: never;
    query?: never;
    url: '/events/banners';
};

export type UploadEventBannerErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UploadEventBannerError = UploadEventBannerErrors[keyof UploadEventBannerErrors];

export type UploadEventBannerResponses = {
    /**
     * Created
     */
    201: FileResponse;
};

export type UploadEventBannerResponse = UploadEventBannerResponses[keyof UploadEventBannerResponses];

export type FindEventSignUpsData = {
    body?: never;
    path?: never;
    query?: {
        from?: string;
        to?: string;
        userId?: number;
        committeeId?: number;
        approved?: boolean;
        eventId?: number;
    };
    url: '/events/signups';
};

export type FindEventSignUpsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindEventSignUpsError = FindEventSignUpsErrors[keyof FindEventSignUpsErrors];

export type FindEventSignUpsResponses = {
    /**
     * OK
     */
    200: Array<EventSignUpResponse>;
};

export type FindEventSignUpsResponse = FindEventSignUpsResponses[keyof FindEventSignUpsResponses];

export type FindEventSignUpsByAccessTokenData = {
    body?: never;
    headers: {
        'X-Guest-Access-Token': string;
    };
    path?: never;
    query?: never;
    url: '/events/signups/byAccessToken';
};

export type FindEventSignUpsByAccessTokenErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindEventSignUpsByAccessTokenError = FindEventSignUpsByAccessTokenErrors[keyof FindEventSignUpsByAccessTokenErrors];

export type FindEventSignUpsByAccessTokenResponses = {
    /**
     * OK
     */
    200: Array<EventSignUpResponse>;
};

export type FindEventSignUpsByAccessTokenResponse = FindEventSignUpsByAccessTokenResponses[keyof FindEventSignUpsByAccessTokenResponses];

export type DeleteEventSignupData = {
    body?: never;
    headers?: {
        'X-Guest-Access-Token'?: string;
    };
    path: {
        id: number;
    };
    query?: {
        notify?: boolean;
    };
    url: '/events/signups/{id}';
};

export type DeleteEventSignupErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteEventSignupError = DeleteEventSignupErrors[keyof DeleteEventSignupErrors];

export type DeleteEventSignupResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteEventSignupResponse = DeleteEventSignupResponses[keyof DeleteEventSignupResponses];

export type UpdateEventSignUpByIdData = {
    body: UpdateEventSignUpRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/events/signups/{id}';
};

export type UpdateEventSignUpByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateEventSignUpByIdError = UpdateEventSignUpByIdErrors[keyof UpdateEventSignUpByIdErrors];

export type UpdateEventSignUpByIdResponses = {
    /**
     * OK
     */
    200: EventSignUpResponse;
};

export type UpdateEventSignUpByIdResponse = UpdateEventSignUpByIdResponses[keyof UpdateEventSignUpByIdResponses];

export type DeleteEventByIdData = {
    body?: never;
    path: {
        eventId: number;
    };
    query?: never;
    url: '/events/{eventId}';
};

export type DeleteEventByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteEventByIdError = DeleteEventByIdErrors[keyof DeleteEventByIdErrors];

export type DeleteEventByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteEventByIdResponse = DeleteEventByIdResponses[keyof DeleteEventByIdResponses];

export type DownloadEventBannerData = {
    body?: never;
    path: {
        eventId: number;
    };
    query?: never;
    url: '/events/{eventId}/banners';
};

export type DownloadEventBannerErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DownloadEventBannerError = DownloadEventBannerErrors[keyof DownloadEventBannerErrors];

export type DownloadEventBannerResponses = {
    /**
     * OK
     */
    200: Blob | File;
};

export type DownloadEventBannerResponse = DownloadEventBannerResponses[keyof DownloadEventBannerResponses];

export type FindEventSignUpsByEventIdData = {
    body?: never;
    path: {
        eventId: number;
    };
    query?: never;
    url: '/events/{eventId}/signups';
};

export type FindEventSignUpsByEventIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindEventSignUpsByEventIdError = FindEventSignUpsByEventIdErrors[keyof FindEventSignUpsByEventIdErrors];

export type FindEventSignUpsByEventIdResponses = {
    /**
     * OK
     */
    200: Array<EventSignUpResponse>;
};

export type FindEventSignUpsByEventIdResponse = FindEventSignUpsByEventIdResponses[keyof FindEventSignUpsByEventIdResponses];

export type CreateEventSignupData = {
    body: CreateEventSignUpRequest;
    path: {
        eventId: number;
    };
    query?: {
        notify?: boolean;
    };
    url: '/events/{eventId}/signups';
};

export type CreateEventSignupErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateEventSignupError = CreateEventSignupErrors[keyof CreateEventSignupErrors];

export type CreateEventSignupResponses = {
    /**
     * Created
     */
    201: EventSignUpResponse;
};

export type CreateEventSignupResponse = CreateEventSignupResponses[keyof CreateEventSignupResponses];

export type UpdateEventSignUpData = {
    body: UpdateEventSignUpRequest;
    headers?: {
        'X-Guest-Access-Token'?: string;
    };
    path: {
        eventId: number;
    };
    query?: never;
    url: '/events/{eventId}/signups';
};

export type UpdateEventSignUpErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateEventSignUpError = UpdateEventSignUpErrors[keyof UpdateEventSignUpErrors];

export type UpdateEventSignUpResponses = {
    /**
     * OK
     */
    200: EventSignUpResponse;
};

export type UpdateEventSignUpResponse = UpdateEventSignUpResponses[keyof UpdateEventSignUpResponses];

export type FindEventByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/events/{id}';
};

export type FindEventByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindEventByIdError = FindEventByIdErrors[keyof FindEventByIdErrors];

export type FindEventByIdResponses = {
    /**
     * OK
     */
    200: EventResponse;
};

export type FindEventByIdResponse = FindEventByIdResponses[keyof FindEventByIdResponses];

export type UpdateEventData = {
    body: UpdateEventRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/events/{id}';
};

export type UpdateEventErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateEventError = UpdateEventErrors[keyof UpdateEventErrors];

export type UpdateEventResponses = {
    /**
     * OK
     */
    200: EventResponse;
};

export type UpdateEventResponse = UpdateEventResponses[keyof UpdateEventResponses];

export type ApproveEventData = {
    body?: never;
    path: {
        id: number;
    };
    query: {
        approved: boolean;
        announce?: AnnounceChoice;
    };
    url: '/events/{id}/approve';
};

export type ApproveEventErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ApproveEventError = ApproveEventErrors[keyof ApproveEventErrors];

export type ApproveEventResponses = {
    /**
     * OK
     */
    200: EventResponse;
};

export type ApproveEventResponse = ApproveEventResponses[keyof ApproveEventResponses];

export type FindEventRosterData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/events/{id}/roster';
};

export type FindEventRosterErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindEventRosterError = FindEventRosterErrors[keyof FindEventRosterErrors];

export type FindEventRosterResponses = {
    /**
     * OK
     */
    200: EventRosterResponse;
};

export type FindEventRosterResponse = FindEventRosterResponses[keyof FindEventRosterResponses];

export type UploadPublicImageData = {
    body: {
        file: Blob | File;
    };
    path?: never;
    query: {
        type: FileType;
    };
    url: '/files/images';
};

export type UploadPublicImageErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UploadPublicImageError = UploadPublicImageErrors[keyof UploadPublicImageErrors];

export type UploadPublicImageResponses = {
    /**
     * Created
     */
    201: Image;
};

export type UploadPublicImageResponse = UploadPublicImageResponses[keyof UploadPublicImageResponses];

export type DownloadPublicFileData = {
    body?: never;
    path: {
        directory: string;
        filename: string;
    };
    query?: never;
    url: '/files/public/{directory}/{filename}';
};

export type DownloadPublicFileErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DownloadPublicFileError = DownloadPublicFileErrors[keyof DownloadPublicFileErrors];

export type DownloadPublicFileResponses = {
    /**
     * OK
     */
    200: Blob | File;
};

export type DownloadPublicFileResponse = DownloadPublicFileResponses[keyof DownloadPublicFileResponses];

export type FindCasualGamesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/games';
};

export type FindCasualGamesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCasualGamesError = FindCasualGamesErrors[keyof FindCasualGamesErrors];

export type FindCasualGamesResponses = {
    /**
     * OK
     */
    200: Array<CasualGameResponse>;
};

export type FindCasualGamesResponse = FindCasualGamesResponses[keyof FindCasualGamesResponses];

export type CreateCasualGameData = {
    body: CasualGameRequest;
    path?: never;
    query?: never;
    url: '/games';
};

export type CreateCasualGameErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateCasualGameError = CreateCasualGameErrors[keyof CreateCasualGameErrors];

export type CreateCasualGameResponses = {
    /**
     * Created
     */
    201: CasualGameResponse;
};

export type CreateCasualGameResponse = CreateCasualGameResponses[keyof CreateCasualGameResponses];

export type RemoveGameData = {
    body?: never;
    path: {
        game: string;
    };
    query?: never;
    url: '/games/{game}';
};

export type RemoveGameErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RemoveGameError = RemoveGameErrors[keyof RemoveGameErrors];

export type RemoveGameResponses = {
    /**
     * No Content
     */
    204: void;
};

export type RemoveGameResponse = RemoveGameResponses[keyof RemoveGameResponses];

export type UpdateCasualGameData = {
    body: CasualGameRequest;
    path: {
        game: string;
    };
    query?: never;
    url: '/games/{game}';
};

export type UpdateCasualGameErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateCasualGameError = UpdateCasualGameErrors[keyof UpdateCasualGameErrors];

export type UpdateCasualGameResponses = {
    /**
     * OK
     */
    200: CasualGameResponse;
};

export type UpdateCasualGameResponse = UpdateCasualGameResponses[keyof UpdateCasualGameResponses];

export type ArchiveGameData = {
    body: ArchiveGameRequest;
    path: {
        game: string;
    };
    query?: never;
    url: '/games/{game}/archived';
};

export type ArchiveGameErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ArchiveGameError = ArchiveGameErrors[keyof ArchiveGameErrors];

export type ArchiveGameResponses = {
    /**
     * OK
     */
    200: CasualGameResponse;
};

export type ArchiveGameResponse = ArchiveGameResponses[keyof ArchiveGameResponses];

export type FindGameHoldingsData = {
    body?: never;
    path: {
        game: string;
    };
    query?: never;
    url: '/games/{game}/holdings';
};

export type FindGameHoldingsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindGameHoldingsError = FindGameHoldingsErrors[keyof FindGameHoldingsErrors];

export type FindGameHoldingsResponses = {
    /**
     * OK
     */
    200: GameHoldingsResponse;
};

export type FindGameHoldingsResponse = FindGameHoldingsResponses[keyof FindGameHoldingsResponses];

export type HealthCheckData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/health';
};

export type HealthCheckErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type HealthCheckError = HealthCheckErrors[keyof HealthCheckErrors];

export type HealthCheckResponses = {
    /**
     * OK
     */
    200: boolean;
};

export type HealthCheckResponse = HealthCheckResponses[keyof HealthCheckResponses];

export type FindCohortSubjectsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/management/cohort-subjects';
};

export type FindCohortSubjectsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCohortSubjectsError = FindCohortSubjectsErrors[keyof FindCohortSubjectsErrors];

export type FindCohortSubjectsResponses = {
    /**
     * OK
     */
    200: Array<CohortSubjectSummary>;
};

export type FindCohortSubjectsResponse = FindCohortSubjectsResponses[keyof FindCohortSubjectsResponses];

export type FindCohortSubjectByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/cohort-subjects/{id}';
};

export type FindCohortSubjectByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCohortSubjectByIdError = FindCohortSubjectByIdErrors[keyof FindCohortSubjectByIdErrors];

export type FindCohortSubjectByIdResponses = {
    /**
     * OK
     */
    200: CohortSubjectDetail;
};

export type FindCohortSubjectByIdResponse = FindCohortSubjectByIdResponses[keyof FindCohortSubjectByIdResponses];

export type LinkUserData = {
    body: LinkUser;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/cohort-subjects/{id}/drift/link-user';
};

export type LinkUserErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type LinkUserError = LinkUserErrors[keyof LinkUserErrors];

export type LinkUserResponses = {
    /**
     * OK
     */
    200: LinkedUser;
};

export type LinkUserResponse = LinkUserResponses[keyof LinkUserResponses];

export type LinkExistingTargetData = {
    body: LinkExistingTarget;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/cohort-subjects/{id}/targets/existing';
};

export type LinkExistingTargetErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type LinkExistingTargetError = LinkExistingTargetErrors[keyof LinkExistingTargetErrors];

export type LinkExistingTargetResponses = {
    /**
     * OK
     */
    200: CohortMapping;
};

export type LinkExistingTargetResponse = LinkExistingTargetResponses[keyof LinkExistingTargetResponses];

export type CreateTargetData = {
    body: CreateTarget;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/cohort-subjects/{id}/targets/new';
};

export type CreateTargetErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateTargetError = CreateTargetErrors[keyof CreateTargetErrors];

export type CreateTargetResponses = {
    /**
     * OK
     */
    200: CohortMapping;
};

export type CreateTargetResponse = CreateTargetResponses[keyof CreateTargetResponses];

export type SwitchTargetData = {
    body: SwitchTarget;
    path: {
        id: number;
        cohortId: number;
    };
    query?: never;
    url: '/management/cohort-subjects/{id}/targets/{cohortId}';
};

export type SwitchTargetErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SwitchTargetError = SwitchTargetErrors[keyof SwitchTargetErrors];

export type SwitchTargetResponses = {
    /**
     * OK
     */
    200: CohortMapping;
};

export type SwitchTargetResponse = SwitchTargetResponses[keyof SwitchTargetResponses];

export type ApplyInboundReconcileData = {
    body: InboundReconcileApplyRequest;
    path: {
        id: number;
        cohortId: number;
    };
    query?: never;
    url: '/management/cohort-subjects/{id}/targets/{cohortId}/inbound-reconcile/apply';
};

export type ApplyInboundReconcileErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ApplyInboundReconcileError = ApplyInboundReconcileErrors[keyof ApplyInboundReconcileErrors];

export type ApplyInboundReconcileResponses = {
    /**
     * OK
     */
    200: InboundReconcileApplyResponse;
};

export type ApplyInboundReconcileResponse = ApplyInboundReconcileResponses[keyof ApplyInboundReconcileResponses];

export type PreviewInboundReconcileData = {
    body?: never;
    path: {
        id: number;
        cohortId: number;
    };
    query?: never;
    url: '/management/cohort-subjects/{id}/targets/{cohortId}/inbound-reconcile/preview';
};

export type PreviewInboundReconcileErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PreviewInboundReconcileError = PreviewInboundReconcileErrors[keyof PreviewInboundReconcileErrors];

export type PreviewInboundReconcileResponses = {
    /**
     * OK
     */
    200: InboundReconcilePreview;
};

export type PreviewInboundReconcileResponse = PreviewInboundReconcileResponses[keyof PreviewInboundReconcileResponses];

export type ListCohortTargetSystemsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/management/cohort-targets/systems';
};

export type ListCohortTargetSystemsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ListCohortTargetSystemsError = ListCohortTargetSystemsErrors[keyof ListCohortTargetSystemsErrors];

export type ListCohortTargetSystemsResponses = {
    /**
     * OK
     */
    200: Array<TargetDescriptor>;
};

export type ListCohortTargetSystemsResponse = ListCohortTargetSystemsResponses[keyof ListCohortTargetSystemsResponses];

export type SearchCohortTargetsData = {
    body?: never;
    path: {
        system: TargetSystem;
    };
    query?: {
        query?: string;
    };
    url: '/management/cohort-targets/{system}';
};

export type SearchCohortTargetsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SearchCohortTargetsError = SearchCohortTargetsErrors[keyof SearchCohortTargetsErrors];

export type SearchCohortTargetsResponses = {
    /**
     * OK
     */
    200: Array<ExternalTarget>;
};

export type SearchCohortTargetsResponse = SearchCohortTargetsResponses[keyof SearchCohortTargetsResponses];

export type MoveCohortTargetsData = {
    body: BulkMoveTargetsRequest;
    path: {
        system: TargetSystem;
    };
    query?: never;
    url: '/management/cohort-targets/{system}/folder';
};

export type MoveCohortTargetsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type MoveCohortTargetsError = MoveCohortTargetsErrors[keyof MoveCohortTargetsErrors];

export type MoveCohortTargetsResponses = {
    /**
     * OK
     */
    200: BulkTargetMoveResult;
};

export type MoveCohortTargetsResponse = MoveCohortTargetsResponses[keyof MoveCohortTargetsResponses];

export type ListCohortTargetFoldersData = {
    body?: never;
    path: {
        system: TargetSystem;
    };
    query?: never;
    url: '/management/cohort-targets/{system}/folders';
};

export type ListCohortTargetFoldersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ListCohortTargetFoldersError = ListCohortTargetFoldersErrors[keyof ListCohortTargetFoldersErrors];

export type ListCohortTargetFoldersResponses = {
    /**
     * OK
     */
    200: Array<string>;
};

export type ListCohortTargetFoldersResponse = ListCohortTargetFoldersResponses[keyof ListCohortTargetFoldersResponses];

export type MoveCohortTargetData = {
    body: MoveTargetRequest;
    path: {
        system: TargetSystem;
        externalId: string;
    };
    query?: never;
    url: '/management/cohort-targets/{system}/{externalId}/folder';
};

export type MoveCohortTargetErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type MoveCohortTargetError = MoveCohortTargetErrors[keyof MoveCohortTargetErrors];

export type MoveCohortTargetResponses = {
    /**
     * OK
     */
    200: ExternalTarget;
};

export type MoveCohortTargetResponse = MoveCohortTargetResponses[keyof MoveCohortTargetResponses];

export type FindCohortsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/management/cohorts';
};

export type FindCohortsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCohortsError = FindCohortsErrors[keyof FindCohortsErrors];

export type FindCohortsResponses = {
    /**
     * OK
     */
    200: Array<CohortSummary>;
};

export type FindCohortsResponse = FindCohortsResponses[keyof FindCohortsResponses];

export type FindCohortByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/cohorts/{id}';
};

export type FindCohortByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindCohortByIdError = FindCohortByIdErrors[keyof FindCohortByIdErrors];

export type FindCohortByIdResponses = {
    /**
     * OK
     */
    200: CohortDetail;
};

export type FindCohortByIdResponse = FindCohortByIdResponses[keyof FindCohortByIdResponses];

export type RepairMissingAddsData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/cohorts/{id}/repair-missing-adds';
};

export type RepairMissingAddsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RepairMissingAddsError = RepairMissingAddsErrors[keyof RepairMissingAddsErrors];

export type RepairMissingAddsResponses = {
    /**
     * OK
     */
    200: CohortRepair;
};

export type RepairMissingAddsResponse = RepairMissingAddsResponses[keyof RepairMissingAddsResponses];

export type List1Data = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Zero-based page index (0..N)
         */
        page?: number;
        /**
         * The size of the page to be returned
         */
        size?: number;
        /**
         * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
         */
        sort?: Array<string>;
        deliveryStatus?: EmailDeliveryStatus;
        emailType?: string;
        search?: string;
    };
    url: '/management/emails';
};

export type List1Errors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type List1Error = List1Errors[keyof List1Errors];

export type List1Responses = {
    /**
     * OK
     */
    200: PagedModelEmail;
};

export type List1Response = List1Responses[keyof List1Responses];

export type GetStats1Data = {
    body?: never;
    path?: never;
    query?: never;
    url: '/management/emails/stats';
};

export type GetStats1Errors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type GetStats1Error = GetStats1Errors[keyof GetStats1Errors];

export type GetStats1Responses = {
    /**
     * OK
     */
    200: EmailStats;
};

export type GetStats1Response = GetStats1Responses[keyof GetStats1Responses];

export type PreviewSentEmailData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/emails/{id}/preview';
};

export type PreviewSentEmailErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PreviewSentEmailError = PreviewSentEmailErrors[keyof PreviewSentEmailErrors];

export type PreviewSentEmailResponses = {
    /**
     * OK
     */
    200: SentEmailPreview;
};

export type PreviewSentEmailResponse = PreviewSentEmailResponses[keyof PreviewSentEmailResponses];

export type Retry1Data = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/emails/{id}/retry';
};

export type Retry1Errors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type Retry1Error = Retry1Errors[keyof Retry1Errors];

export type Retry1Responses = {
    /**
     * OK
     */
    200: Email;
};

export type Retry1Response = Retry1Responses[keyof Retry1Responses];

export type ListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Zero-based page index (0..N)
         */
        page?: number;
        /**
         * The size of the page to be returned
         */
        size?: number;
        /**
         * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
         */
        sort?: Array<string>;
        status?: JobExecutionStatus;
        category?: JobExecutionCategory;
        search?: string;
        initiatedByType?: ActionActorType;
        jobType?: string;
        hideSkipped?: boolean;
    };
    url: '/management/jobs';
};

export type ListErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ListError = ListErrors[keyof ListErrors];

export type ListResponses = {
    /**
     * OK
     */
    200: PagedModelJobExecution;
};

export type ListResponse = ListResponses[keyof ListResponses];

export type EnqueueData = {
    body: EnqueueJobRequest;
    path?: never;
    query?: never;
    url: '/management/jobs/enqueue';
};

export type EnqueueErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type EnqueueError = EnqueueErrors[keyof EnqueueErrors];

export type EnqueueResponses = {
    /**
     * OK
     */
    200: JobExecution;
};

export type EnqueueResponse = EnqueueResponses[keyof EnqueueResponses];

export type GetStatsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/management/jobs/stats';
};

export type GetStatsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type GetStatsError = GetStatsErrors[keyof GetStatsErrors];

export type GetStatsResponses = {
    /**
     * OK
     */
    200: JobStatsDto;
};

export type GetStatsResponse = GetStatsResponses[keyof GetStatsResponses];

export type JobTypesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/management/jobs/types';
};

export type JobTypesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type JobTypesError = JobTypesErrors[keyof JobTypesErrors];

export type JobTypesResponses = {
    /**
     * OK
     */
    200: Array<JobTypeDescriptor>;
};

export type JobTypesResponse = JobTypesResponses[keyof JobTypesResponses];

export type RetryData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/management/jobs/{id}/retry';
};

export type RetryErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RetryError = RetryErrors[keyof RetryErrors];

export type RetryResponses = {
    /**
     * OK
     */
    200: JobExecution;
};

export type RetryResponse = RetryResponses[keyof RetryResponses];

export type MyServicesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/me/services';
};

export type MyServicesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type MyServicesError = MyServicesErrors[keyof MyServicesErrors];

export type MyServicesResponses = {
    /**
     * OK
     */
    200: Array<ServiceEntry>;
};

export type MyServicesResponse = MyServicesResponses[keyof MyServicesResponses];

export type CreateMemberProfileData = {
    body: CreateMemberProfileRequest;
    path?: never;
    query?: never;
    url: '/memberProfiles';
};

export type CreateMemberProfileErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateMemberProfileError = CreateMemberProfileErrors[keyof CreateMemberProfileErrors];

export type CreateMemberProfileResponses = {
    /**
     * Created
     */
    201: MemberProfileResponse;
};

export type CreateMemberProfileResponse = CreateMemberProfileResponses[keyof CreateMemberProfileResponses];

export type FindMembershipsData = {
    body?: never;
    path?: never;
    query?: {
        from?: string;
        to?: string;
        userId?: number;
    };
    url: '/memberships';
};

export type FindMembershipsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindMembershipsError = FindMembershipsErrors[keyof FindMembershipsErrors];

export type FindMembershipsResponses = {
    /**
     * OK
     */
    200: Array<MembershipResponse>;
};

export type FindMembershipsResponse = FindMembershipsResponses[keyof FindMembershipsResponses];

export type CreateMembershipData = {
    body: MembershipApplicationRequest;
    path?: never;
    query?: never;
    url: '/memberships';
};

export type CreateMembershipErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateMembershipError = CreateMembershipErrors[keyof CreateMembershipErrors];

export type CreateMembershipResponses = {
    /**
     * OK
     */
    200: SignupOutcomeResponse;
};

export type CreateMembershipResponse = CreateMembershipResponses[keyof CreateMembershipResponses];

export type EndMembershipsData = {
    body: BulkMembershipRequest;
    path?: never;
    query?: never;
    url: '/memberships/bulk/end';
};

export type EndMembershipsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type EndMembershipsError = EndMembershipsErrors[keyof EndMembershipsErrors];

export type EndMembershipsResponses = {
    /**
     * OK
     */
    200: BulkActionResult;
};

export type EndMembershipsResponse = EndMembershipsResponses[keyof EndMembershipsResponses];

export type PreviewBulkEndData = {
    body: BulkMembershipRequest;
    path?: never;
    query?: never;
    url: '/memberships/bulk/end/preview';
};

export type PreviewBulkEndErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PreviewBulkEndError = PreviewBulkEndErrors[keyof PreviewBulkEndErrors];

export type PreviewBulkEndResponses = {
    /**
     * OK
     */
    200: BulkMembershipPreview;
};

export type PreviewBulkEndResponse = PreviewBulkEndResponses[keyof PreviewBulkEndResponses];

export type StartMembershipsData = {
    body: BulkMembershipRequest;
    path?: never;
    query?: never;
    url: '/memberships/bulk/start';
};

export type StartMembershipsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type StartMembershipsError = StartMembershipsErrors[keyof StartMembershipsErrors];

export type StartMembershipsResponses = {
    /**
     * OK
     */
    200: BulkActionResult;
};

export type StartMembershipsResponse = StartMembershipsResponses[keyof StartMembershipsResponses];

export type PreviewBulkStartData = {
    body: BulkMembershipRequest;
    path?: never;
    query?: never;
    url: '/memberships/bulk/start/preview';
};

export type PreviewBulkStartErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PreviewBulkStartError = PreviewBulkStartErrors[keyof PreviewBulkStartErrors];

export type PreviewBulkStartResponses = {
    /**
     * OK
     */
    200: BulkMembershipPreview;
};

export type PreviewBulkStartResponse = PreviewBulkStartResponses[keyof PreviewBulkStartResponses];

export type DeleteMembershipData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/memberships/{id}';
};

export type DeleteMembershipErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteMembershipError = DeleteMembershipErrors[keyof DeleteMembershipErrors];

export type DeleteMembershipResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteMembershipResponse = DeleteMembershipResponses[keyof DeleteMembershipResponses];

export type FindMembershipByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/memberships/{id}';
};

export type FindMembershipByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindMembershipByIdError = FindMembershipByIdErrors[keyof FindMembershipByIdErrors];

export type FindMembershipByIdResponses = {
    /**
     * OK
     */
    200: MembershipResponse;
};

export type FindMembershipByIdResponse = FindMembershipByIdResponses[keyof FindMembershipByIdResponses];

export type UpdateMembershipData = {
    body: UpdateMembershipRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/memberships/{id}';
};

export type UpdateMembershipErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateMembershipError = UpdateMembershipErrors[keyof UpdateMembershipErrors];

export type UpdateMembershipResponses = {
    /**
     * OK
     */
    200: MembershipResponse;
};

export type UpdateMembershipResponse = UpdateMembershipResponses[keyof UpdateMembershipResponses];

export type EndMembershipData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/memberships/{id}/end';
};

export type EndMembershipErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type EndMembershipError = EndMembershipErrors[keyof EndMembershipErrors];

export type EndMembershipResponses = {
    /**
     * OK
     */
    200: MembershipResponse;
};

export type EndMembershipResponse = EndMembershipResponses[keyof EndMembershipResponses];

export type ReopenMembershipData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/memberships/{id}/reopen';
};

export type ReopenMembershipErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ReopenMembershipError = ReopenMembershipErrors[keyof ReopenMembershipErrors];

export type ReopenMembershipResponses = {
    /**
     * OK
     */
    200: MembershipResponse;
};

export type ReopenMembershipResponse = ReopenMembershipResponses[keyof ReopenMembershipResponses];

export type RestoreMembershipData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/memberships/{id}/restore';
};

export type RestoreMembershipErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RestoreMembershipError = RestoreMembershipErrors[keyof RestoreMembershipErrors];

export type RestoreMembershipResponses = {
    /**
     * OK
     */
    200: MembershipResponse;
};

export type RestoreMembershipResponse = RestoreMembershipResponses[keyof RestoreMembershipResponses];

export type ForwardAuthData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/oauth2/forward-auth';
};

export type ForwardAuthErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ForwardAuthError = ForwardAuthErrors[keyof ForwardAuthErrors];

export type ForwardAuthResponses = {
    /**
     * OK
     */
    200: unknown;
};

export type BoardData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/pinger/leaderboard';
};

export type BoardErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type BoardError = BoardErrors[keyof BoardErrors];

export type BoardResponses = {
    /**
     * OK
     */
    200: LeaderboardResponse;
};

export type BoardResponse2 = BoardResponses[keyof BoardResponses];

export type ConfirmEmailChangeData = {
    body: TokenRequest;
    path?: never;
    query?: never;
    url: '/recovery/email/confirm';
};

export type ConfirmEmailChangeErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ConfirmEmailChangeError = ConfirmEmailChangeErrors[keyof ConfirmEmailChangeErrors];

export type ConfirmEmailChangeResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ConfirmEmailChangeResponse = ConfirmEmailChangeResponses[keyof ConfirmEmailChangeResponses];

export type LockData = {
    body: TokenRequest;
    path?: never;
    query?: never;
    url: '/recovery/lock';
};

export type LockErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type LockError = LockErrors[keyof LockErrors];

export type LockResponses = {
    /**
     * OK
     */
    200: LockResponse;
};

export type LockResponse2 = LockResponses[keyof LockResponses];

export type MemberActivateData = {
    body: MemberActivationRequest;
    path?: never;
    query?: never;
    url: '/recovery/member/activate';
};

export type MemberActivateErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type MemberActivateError = MemberActivateErrors[keyof MemberActivateErrors];

export type MemberActivateResponses = {
    /**
     * OK
     */
    200: RedirectResponse;
};

export type MemberActivateResponse = MemberActivateResponses[keyof MemberActivateResponses];

export type SetPasswordData = {
    body: PasswordResetRequest;
    path?: never;
    query?: never;
    url: '/recovery/password';
};

export type SetPasswordErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SetPasswordError = SetPasswordErrors[keyof SetPasswordErrors];

export type SetPasswordResponses = {
    /**
     * No Content
     */
    204: void;
};

export type SetPasswordResponse = SetPasswordResponses[keyof SetPasswordResponses];

export type ResetPasswordData = {
    body?: never;
    path: {
        username: string;
    };
    query?: never;
    url: '/recovery/password/reset/{username}';
};

export type ResetPasswordErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ResetPasswordError = ResetPasswordErrors[keyof ResetPasswordErrors];

export type ResetPasswordResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ResetPasswordResponse = ResetPasswordResponses[keyof ResetPasswordResponses];

export type PendingActivationsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/recovery/pending-activations';
};

export type PendingActivationsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PendingActivationsError = PendingActivationsErrors[keyof PendingActivationsErrors];

export type PendingActivationsResponses = {
    /**
     * OK
     */
    200: PendingActivationsResponse;
};

export type PendingActivationsResponse2 = PendingActivationsResponses[keyof PendingActivationsResponses];

export type ReenrolData = {
    body: ReenrolRequest;
    path?: never;
    query?: never;
    url: '/recovery/two-factor/re-enrol';
};

export type ReenrolErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ReenrolError = ReenrolErrors[keyof ReenrolErrors];

export type ReenrolResponses = {
    /**
     * OK
     */
    200: SignInAnswer;
};

export type ReenrolResponse = ReenrolResponses[keyof ReenrolResponses];

export type UserActivateData = {
    body: UserActivationRequest;
    path?: never;
    query?: never;
    url: '/recovery/user/activate';
};

export type UserActivateErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UserActivateError = UserActivateErrors[keyof UserActivateErrors];

export type UserActivateResponses = {
    /**
     * OK
     */
    200: ActivationResponse;
};

export type UserActivateResponse = UserActivateResponses[keyof UserActivateResponses];

export type ResendUserActivationData = {
    body?: never;
    path: {
        username: string;
    };
    query?: never;
    url: '/recovery/user/activate/resend/{username}';
};

export type ResendUserActivationErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ResendUserActivationError = ResendUserActivationErrors[keyof ResendUserActivationErrors];

export type ResendUserActivationResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ResendUserActivationResponse = ResendUserActivationResponses[keyof ResendUserActivationResponses];

export type PreviewRecoveryEmailData = {
    body?: never;
    path: {
        userId: number;
    };
    query: {
        purpose: TokenPurpose;
    };
    url: '/recovery/users/{userId}/email-preview';
};

export type PreviewRecoveryEmailErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type PreviewRecoveryEmailError = PreviewRecoveryEmailErrors[keyof PreviewRecoveryEmailErrors];

export type PreviewRecoveryEmailResponses = {
    /**
     * OK
     */
    200: RecoveryEmailPreviewResponse;
};

export type PreviewRecoveryEmailResponse = PreviewRecoveryEmailResponses[keyof PreviewRecoveryEmailResponses];

export type ResendRecoveryEmailData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: {
        purpose?: TokenPurpose;
    };
    url: '/recovery/users/{userId}/resend/recovery';
};

export type ResendRecoveryEmailErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ResendRecoveryEmailError = ResendRecoveryEmailErrors[keyof ResendRecoveryEmailErrors];

export type ResendRecoveryEmailResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ResendRecoveryEmailResponse = ResendRecoveryEmailResponses[keyof ResendRecoveryEmailResponses];

export type SignUpData = {
    body: CreateUserRequest;
    path?: never;
    query?: never;
    url: '/signup';
};

export type SignUpErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SignUpError = SignUpErrors[keyof SignUpErrors];

export type SignUpResponses = {
    /**
     * Created
     */
    201: SignupSessionResponse;
};

export type SignUpResponse = SignUpResponses[keyof SignUpResponses];

export type SaveAddressData = {
    body: SignupAddressRequest;
    headers: {
        'X-Signup-Token': string;
    };
    path?: never;
    query?: never;
    url: '/signup/address';
};

export type SaveAddressErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SaveAddressError = SaveAddressErrors[keyof SaveAddressErrors];

export type SaveAddressResponses = {
    /**
     * No Content
     */
    204: void;
};

export type SaveAddressResponse = SaveAddressResponses[keyof SaveAddressResponses];

export type ApplyData = {
    body: SignupApplicationRequest;
    headers: {
        'X-Signup-Token': string;
    };
    path?: never;
    query?: never;
    url: '/signup/apply';
};

export type ApplyErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ApplyError = ApplyErrors[keyof ApplyErrors];

export type ApplyResponses = {
    /**
     * OK
     */
    200: SignupOutcomeResponse;
};

export type ApplyResponse = ApplyResponses[keyof ApplyResponses];

export type UpdateDetailsData = {
    body: SignupDetailsRequest;
    headers: {
        'X-Signup-Token': string;
    };
    path?: never;
    query?: never;
    url: '/signup/details';
};

export type UpdateDetailsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateDetailsError = UpdateDetailsErrors[keyof UpdateDetailsErrors];

export type UpdateDetailsResponses = {
    /**
     * No Content
     */
    204: void;
};

export type UpdateDetailsResponse = UpdateDetailsResponses[keyof UpdateDetailsResponses];

export type CorrectEmailData = {
    body: SignupEmailRequest;
    headers: {
        'X-Signup-Token': string;
    };
    path?: never;
    query?: never;
    url: '/signup/email';
};

export type CorrectEmailErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CorrectEmailError = CorrectEmailErrors[keyof CorrectEmailErrors];

export type CorrectEmailResponses = {
    /**
     * No Content
     */
    204: void;
};

export type CorrectEmailResponse = CorrectEmailResponses[keyof CorrectEmailResponses];

export type ResumeSignupData = {
    body?: never;
    headers: {
        'X-Signup-Token': string;
    };
    path?: never;
    query?: never;
    url: '/signup/session';
};

export type ResumeSignupErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ResumeSignupError = ResumeSignupErrors[keyof ResumeSignupErrors];

export type ResumeSignupResponses = {
    /**
     * OK
     */
    200: SignupResumeResponse;
};

export type ResumeSignupResponse = ResumeSignupResponses[keyof ResumeSignupResponses];

export type FindSponsorsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/sponsors';
};

export type FindSponsorsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindSponsorsError = FindSponsorsErrors[keyof FindSponsorsErrors];

export type FindSponsorsResponses = {
    /**
     * OK
     */
    200: Array<SponsorResponse>;
};

export type FindSponsorsResponse = FindSponsorsResponses[keyof FindSponsorsResponses];

export type CreateSponsorData = {
    body: CreateSponsorRequest;
    path?: never;
    query?: never;
    url: '/sponsors';
};

export type CreateSponsorErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateSponsorError = CreateSponsorErrors[keyof CreateSponsorErrors];

export type CreateSponsorResponses = {
    /**
     * Created
     */
    201: SponsorResponse;
};

export type CreateSponsorResponse = CreateSponsorResponses[keyof CreateSponsorResponses];

export type DeleteSponsorByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/sponsors/{id}';
};

export type DeleteSponsorByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteSponsorByIdError = DeleteSponsorByIdErrors[keyof DeleteSponsorByIdErrors];

export type DeleteSponsorByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteSponsorByIdResponse = DeleteSponsorByIdResponses[keyof DeleteSponsorByIdResponses];

export type FindSponsorByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/sponsors/{id}';
};

export type FindSponsorByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindSponsorByIdError = FindSponsorByIdErrors[keyof FindSponsorByIdErrors];

export type FindSponsorByIdResponses = {
    /**
     * OK
     */
    200: SponsorResponse;
};

export type FindSponsorByIdResponse = FindSponsorByIdResponses[keyof FindSponsorByIdResponses];

export type UpdateSponsorData = {
    body: UpdateSponsorRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/sponsors/{id}';
};

export type UpdateSponsorErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateSponsorError = UpdateSponsorErrors[keyof UpdateSponsorErrors];

export type UpdateSponsorResponses = {
    /**
     * OK
     */
    200: SponsorResponse;
};

export type UpdateSponsorResponse = UpdateSponsorResponses[keyof UpdateSponsorResponses];

export type AssociationStatisticsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/statistics/association';
};

export type AssociationStatisticsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type AssociationStatisticsError = AssociationStatisticsErrors[keyof AssociationStatisticsErrors];

export type AssociationStatisticsResponses = {
    /**
     * OK
     */
    200: AssociationStatisticsResponse;
};

export type AssociationStatisticsResponse2 = AssociationStatisticsResponses[keyof AssociationStatisticsResponses];

export type CreateTelemetryData = {
    body: CreateTelemetryRequest;
    path?: never;
    query?: never;
    url: '/telemetry';
};

export type CreateTelemetryErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateTelemetryError = CreateTelemetryErrors[keyof CreateTelemetryErrors];

export type CreateTelemetryResponses = {
    /**
     * Created
     */
    201: TelemetryResponse;
};

export type CreateTelemetryResponse = CreateTelemetryResponses[keyof CreateTelemetryResponses];

export type FindTelemetryByIdData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/telemetry/{id}';
};

export type FindTelemetryByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindTelemetryByIdError = FindTelemetryByIdErrors[keyof FindTelemetryByIdErrors];

export type FindTelemetryByIdResponses = {
    /**
     * OK
     */
    200: TelemetryResponse;
};

export type FindTelemetryByIdResponse = FindTelemetryByIdResponses[keyof FindTelemetryByIdResponses];

export type FindUsersData = {
    body?: never;
    path?: never;
    query?: {
        username?: string;
        enabled?: boolean;
        search?: string;
        /**
         * Zero-based page index (0..N)
         */
        page?: number;
        /**
         * The size of the page to be returned
         */
        size?: number;
        /**
         * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
         */
        sort?: Array<string>;
    };
    url: '/users';
};

export type FindUsersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindUsersError = FindUsersErrors[keyof FindUsersErrors];

export type FindUsersResponses = {
    /**
     * OK
     */
    200: PagedModelUserDetailResponse;
};

export type FindUsersResponse = FindUsersResponses[keyof FindUsersResponses];

export type CreateUserData = {
    body: CreateUserRequest;
    path?: never;
    query?: never;
    url: '/users';
};

export type CreateUserErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type CreateUserError = CreateUserErrors[keyof CreateUserErrors];

export type CreateUserResponses = {
    /**
     * Created
     */
    201: UserDetailResponse;
};

export type CreateUserResponse = CreateUserResponses[keyof CreateUserResponses];

export type FindDeletedUsersData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Zero-based page index (0..N)
         */
        page?: number;
        /**
         * The size of the page to be returned
         */
        size?: number;
        /**
         * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
         */
        sort?: Array<string>;
    };
    url: '/users/deleted';
};

export type FindDeletedUsersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindDeletedUsersError = FindDeletedUsersErrors[keyof FindDeletedUsersErrors];

export type FindDeletedUsersResponses = {
    /**
     * OK
     */
    200: PagedModelUserDetailResponse;
};

export type FindDeletedUsersResponse = FindDeletedUsersResponses[keyof FindDeletedUsersResponses];

export type FindOwnContributionsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/contributions';
};

export type FindOwnContributionsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindOwnContributionsError = FindOwnContributionsErrors[keyof FindOwnContributionsErrors];

export type FindOwnContributionsResponses = {
    /**
     * OK
     */
    200: OwnContributions;
};

export type FindOwnContributionsResponse = FindOwnContributionsResponses[keyof FindOwnContributionsResponses];

export type EmailAddressData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/email';
};

export type EmailAddressErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type EmailAddressError = EmailAddressErrors[keyof EmailAddressErrors];

export type EmailAddressResponses = {
    /**
     * OK
     */
    200: EmailAddressResponse;
};

export type EmailAddressResponse2 = EmailAddressResponses[keyof EmailAddressResponses];

export type RequestEmailChangeData = {
    body: EmailChangeRequest;
    path?: never;
    query?: never;
    url: '/users/me/email';
};

export type RequestEmailChangeErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RequestEmailChangeError = RequestEmailChangeErrors[keyof RequestEmailChangeErrors];

export type RequestEmailChangeResponses = {
    /**
     * No Content
     */
    204: void;
};

export type RequestEmailChangeResponse = RequestEmailChangeResponses[keyof RequestEmailChangeResponses];

export type FindOwnFirstContributionData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/first-contribution';
};

export type FindOwnFirstContributionErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindOwnFirstContributionError = FindOwnFirstContributionErrors[keyof FindOwnFirstContributionErrors];

export type FindOwnFirstContributionResponses = {
    /**
     * OK
     */
    200: FirstContribution;
};

export type FindOwnFirstContributionResponse = FindOwnFirstContributionResponses[keyof FindOwnFirstContributionResponses];

export type ChangePasswordData = {
    body: PasswordChangeRequest;
    path?: never;
    query?: never;
    url: '/users/me/password';
};

export type ChangePasswordErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ChangePasswordError = ChangePasswordErrors[keyof ChangePasswordErrors];

export type ChangePasswordResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ChangePasswordResponse = ChangePasswordResponses[keyof ChangePasswordResponses];

export type MySecurityEventsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Zero-based page index (0..N)
         */
        page?: number;
        /**
         * The size of the page to be returned
         */
        size?: number;
        /**
         * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
         */
        sort?: Array<string>;
    };
    url: '/users/me/security-events';
};

export type MySecurityEventsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type MySecurityEventsError = MySecurityEventsErrors[keyof MySecurityEventsErrors];

export type MySecurityEventsResponses = {
    /**
     * OK
     */
    200: SecurityEventPageResponse;
};

export type MySecurityEventsResponse = MySecurityEventsResponses[keyof MySecurityEventsResponses];

export type SignOutEverywhereData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/sign-ins';
};

export type SignOutEverywhereErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SignOutEverywhereError = SignOutEverywhereErrors[keyof SignOutEverywhereErrors];

export type SignOutEverywhereResponses = {
    /**
     * No Content
     */
    204: void;
};

export type SignOutEverywhereResponse = SignOutEverywhereResponses[keyof SignOutEverywhereResponses];

export type SignInsData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/sign-ins';
};

export type SignInsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SignInsError = SignInsErrors[keyof SignInsErrors];

export type SignInsResponses = {
    /**
     * OK
     */
    200: Array<SignInResponse>;
};

export type SignInsResponse = SignInsResponses[keyof SignInsResponses];

export type SignOutElsewhereData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/sign-ins/others';
};

export type SignOutElsewhereErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SignOutElsewhereError = SignOutElsewhereErrors[keyof SignOutElsewhereErrors];

export type SignOutElsewhereResponses = {
    /**
     * No Content
     */
    204: void;
};

export type SignOutElsewhereResponse = SignOutElsewhereResponses[keyof SignOutElsewhereResponses];

export type EndSignInData = {
    body?: never;
    path: {
        signInId: string;
    };
    query?: never;
    url: '/users/me/sign-ins/{signInId}';
};

export type EndSignInErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type EndSignInError = EndSignInErrors[keyof EndSignInErrors];

export type EndSignInResponses = {
    /**
     * No Content
     */
    204: void;
};

export type EndSignInResponse = EndSignInResponses[keyof EndSignInResponses];

export type ForgetTrustedBrowsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/trusted-browsers';
};

export type ForgetTrustedBrowsersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ForgetTrustedBrowsersError = ForgetTrustedBrowsersErrors[keyof ForgetTrustedBrowsersErrors];

export type ForgetTrustedBrowsersResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ForgetTrustedBrowsersResponse = ForgetTrustedBrowsersResponses[keyof ForgetTrustedBrowsersResponses];

export type TrustedBrowsersData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/trusted-browsers';
};

export type TrustedBrowsersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type TrustedBrowsersError = TrustedBrowsersErrors[keyof TrustedBrowsersErrors];

export type TrustedBrowsersResponses = {
    /**
     * OK
     */
    200: Array<TrustedBrowserResponse>;
};

export type TrustedBrowsersResponse = TrustedBrowsersResponses[keyof TrustedBrowsersResponses];

export type ForgetTrustedBrowserData = {
    body?: never;
    path: {
        id: number;
    };
    query?: never;
    url: '/users/me/trusted-browsers/{id}';
};

export type ForgetTrustedBrowserErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ForgetTrustedBrowserError = ForgetTrustedBrowserErrors[keyof ForgetTrustedBrowserErrors];

export type ForgetTrustedBrowserResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ForgetTrustedBrowserResponse = ForgetTrustedBrowserResponses[keyof ForgetTrustedBrowserResponses];

export type TurnOffTwoFactorData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/two-factor';
};

export type TurnOffTwoFactorErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type TurnOffTwoFactorError = TurnOffTwoFactorErrors[keyof TurnOffTwoFactorErrors];

export type TurnOffTwoFactorResponses = {
    /**
     * No Content
     */
    204: void;
};

export type TurnOffTwoFactorResponse = TurnOffTwoFactorResponses[keyof TurnOffTwoFactorResponses];

export type TwoFactorStandingData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/two-factor';
};

export type TwoFactorStandingErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type TwoFactorStandingError = TwoFactorStandingErrors[keyof TwoFactorStandingErrors];

export type TwoFactorStandingResponses = {
    /**
     * OK
     */
    200: TwoFactorStanding;
};

export type TwoFactorStandingResponse = TwoFactorStandingResponses[keyof TwoFactorStandingResponses];

export type RegenerateBackupCodesData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/two-factor/backup-codes';
};

export type RegenerateBackupCodesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RegenerateBackupCodesError = RegenerateBackupCodesErrors[keyof RegenerateBackupCodesErrors];

export type RegenerateBackupCodesResponses = {
    /**
     * OK
     */
    200: BackupCodesResponse;
};

export type RegenerateBackupCodesResponse = RegenerateBackupCodesResponses[keyof RegenerateBackupCodesResponses];

export type ConfirmTwoFactorData = {
    body: CodeRequest;
    path?: never;
    query?: never;
    url: '/users/me/two-factor/confirm';
};

export type ConfirmTwoFactorErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ConfirmTwoFactorError = ConfirmTwoFactorErrors[keyof ConfirmTwoFactorErrors];

export type ConfirmTwoFactorResponses = {
    /**
     * OK
     */
    200: BackupCodesResponse;
};

export type ConfirmTwoFactorResponse = ConfirmTwoFactorResponses[keyof ConfirmTwoFactorResponses];

export type AnswerTwoFactorOfferData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/two-factor/offer';
};

export type AnswerTwoFactorOfferErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type AnswerTwoFactorOfferError = AnswerTwoFactorOfferErrors[keyof AnswerTwoFactorOfferErrors];

export type AnswerTwoFactorOfferResponses = {
    /**
     * No Content
     */
    204: void;
};

export type AnswerTwoFactorOfferResponse = AnswerTwoFactorOfferResponses[keyof AnswerTwoFactorOfferResponses];

export type TwoFactorSavedData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/users/me/two-factor/saved';
};

export type TwoFactorSavedErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type TwoFactorSavedError = TwoFactorSavedErrors[keyof TwoFactorSavedErrors];

export type TwoFactorSavedResponses = {
    /**
     * No Content
     */
    204: void;
};

export type TwoFactorSavedResponse = TwoFactorSavedResponses[keyof TwoFactorSavedResponses];

export type SetUpTwoFactorData = {
    body: TwoFactorSetUpRequest;
    path?: never;
    query?: never;
    url: '/users/me/two-factor/setup';
};

export type SetUpTwoFactorErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SetUpTwoFactorError = SetUpTwoFactorErrors[keyof SetUpTwoFactorErrors];

export type SetUpTwoFactorResponses = {
    /**
     * OK
     */
    200: TwoFactorSetupResponse;
};

export type SetUpTwoFactorResponse = SetUpTwoFactorResponses[keyof SetUpTwoFactorResponses];

export type UpdateUserData = {
    body: UpdateUserRequest;
    path: {
        id: number;
    };
    query?: never;
    url: '/users/{id}';
};

export type UpdateUserErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateUserError = UpdateUserErrors[keyof UpdateUserErrors];

export type UpdateUserResponses = {
    /**
     * OK
     */
    200: UserDetailResponse;
};

export type UpdateUserResponse = UpdateUserResponses[keyof UpdateUserResponses];

export type DeleteUserByIdData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}';
};

export type DeleteUserByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type DeleteUserByIdError = DeleteUserByIdErrors[keyof DeleteUserByIdErrors];

export type DeleteUserByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type DeleteUserByIdResponse = DeleteUserByIdResponses[keyof DeleteUserByIdResponses];

export type FindUserByIdData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}';
};

export type FindUserByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindUserByIdError = FindUserByIdErrors[keyof FindUserByIdErrors];

export type FindUserByIdResponses = {
    /**
     * OK
     */
    200: UserDetailResponse;
};

export type FindUserByIdResponse = FindUserByIdResponses[keyof FindUserByIdResponses];

export type AccountStandingData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/account-security';
};

export type AccountStandingErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type AccountStandingError = AccountStandingErrors[keyof AccountStandingErrors];

export type AccountStandingResponses = {
    /**
     * OK
     */
    200: AccountStandingResponse;
};

export type AccountStandingResponse2 = AccountStandingResponses[keyof AccountStandingResponses];

export type FindGameAccountsData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/game-accounts';
};

export type FindGameAccountsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindGameAccountsError = FindGameAccountsErrors[keyof FindGameAccountsErrors];

export type FindGameAccountsResponses = {
    /**
     * OK
     */
    200: Array<GameAccountResponse>;
};

export type FindGameAccountsResponse = FindGameAccountsResponses[keyof FindGameAccountsResponses];

export type ClearGameAccountData = {
    body?: never;
    path: {
        userId: number;
        game: string;
    };
    query?: never;
    url: '/users/{userId}/game-accounts/{game}';
};

export type ClearGameAccountErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ClearGameAccountError = ClearGameAccountErrors[keyof ClearGameAccountErrors];

export type ClearGameAccountResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ClearGameAccountResponse = ClearGameAccountResponses[keyof ClearGameAccountResponses];

export type SetGameAccountData = {
    body: GameAccountRequest;
    path: {
        userId: number;
        game: string;
    };
    query?: never;
    url: '/users/{userId}/game-accounts/{game}';
};

export type SetGameAccountErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SetGameAccountError = SetGameAccountErrors[keyof SetGameAccountErrors];

export type SetGameAccountResponses = {
    /**
     * OK
     */
    200: GameAccountResponse;
};

export type SetGameAccountResponse = SetGameAccountResponses[keyof SetGameAccountResponses];

export type FindMemberProfileByUserIdData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/memberProfiles';
};

export type FindMemberProfileByUserIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindMemberProfileByUserIdError = FindMemberProfileByUserIdErrors[keyof FindMemberProfileByUserIdErrors];

export type FindMemberProfileByUserIdResponses = {
    /**
     * OK
     */
    200: MemberProfileResponse;
};

export type FindMemberProfileByUserIdResponse = FindMemberProfileByUserIdResponses[keyof FindMemberProfileByUserIdResponses];

export type UpdateMemberProfileData = {
    body: UpdateMemberProfileRequest;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/memberProfiles';
};

export type UpdateMemberProfileErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UpdateMemberProfileError = UpdateMemberProfileErrors[keyof UpdateMemberProfileErrors];

export type UpdateMemberProfileResponses = {
    /**
     * OK
     */
    200: MemberProfileResponse;
};

export type UpdateMemberProfileResponse = UpdateMemberProfileResponses[keyof UpdateMemberProfileResponses];

export type BoardCreateMembershipData = {
    body: BoardCreateMembershipRequest;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/memberships';
};

export type BoardCreateMembershipErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type BoardCreateMembershipError = BoardCreateMembershipErrors[keyof BoardCreateMembershipErrors];

export type BoardCreateMembershipResponses = {
    /**
     * Created
     */
    201: MembershipResponse;
};

export type BoardCreateMembershipResponse = BoardCreateMembershipResponses[keyof BoardCreateMembershipResponses];

export type FindDeletedMembershipsData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/memberships/deleted';
};

export type FindDeletedMembershipsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindDeletedMembershipsError = FindDeletedMembershipsErrors[keyof FindDeletedMembershipsErrors];

export type FindDeletedMembershipsResponses = {
    /**
     * OK
     */
    200: Array<MembershipResponse>;
};

export type FindDeletedMembershipsResponse = FindDeletedMembershipsResponses[keyof FindDeletedMembershipsResponses];

export type SetNameOnRostersData = {
    body: NameOnRostersRequest;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/name-on-rosters';
};

export type SetNameOnRostersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SetNameOnRostersError = SetNameOnRostersErrors[keyof SetNameOnRostersErrors];

export type SetNameOnRostersResponses = {
    /**
     * OK
     */
    200: UserDetailResponse;
};

export type SetNameOnRostersResponse = SetNameOnRostersResponses[keyof SetNameOnRostersResponses];

export type RestoreDeletedUserByIdData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/restore';
};

export type RestoreDeletedUserByIdErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type RestoreDeletedUserByIdError = RestoreDeletedUserByIdErrors[keyof RestoreDeletedUserByIdErrors];

export type RestoreDeletedUserByIdResponses = {
    /**
     * No Content
     */
    204: void;
};

export type RestoreDeletedUserByIdResponse = RestoreDeletedUserByIdResponses[keyof RestoreDeletedUserByIdResponses];

export type FindUserRoleChangesData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/role-changes';
};

export type FindUserRoleChangesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindUserRoleChangesError = FindUserRoleChangesErrors[keyof FindUserRoleChangesErrors];

export type FindUserRoleChangesResponses = {
    /**
     * OK
     */
    200: Array<RoleChangeResponse>;
};

export type FindUserRoleChangesResponse = FindUserRoleChangesResponses[keyof FindUserRoleChangesResponses];

export type FindUserRolesData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/roles';
};

export type FindUserRolesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindUserRolesError = FindUserRolesErrors[keyof FindUserRolesErrors];

export type FindUserRolesResponses = {
    /**
     * OK
     */
    200: UserRolesResponse;
};

export type FindUserRolesResponse = FindUserRolesResponses[keyof FindUserRolesResponses];

export type SetUserRolesData = {
    body: UpdateUserRolesRequest;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/roles';
};

export type SetUserRolesErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SetUserRolesError = SetUserRolesErrors[keyof SetUserRolesErrors];

export type SetUserRolesResponses = {
    /**
     * OK
     */
    200: UserRolesResponse;
};

export type SetUserRolesResponse = SetUserRolesResponses[keyof SetUserRolesResponses];

export type FindPlayedRostersData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/rosters';
};

export type FindPlayedRostersErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type FindPlayedRostersError = FindPlayedRostersErrors[keyof FindPlayedRostersErrors];

export type FindPlayedRostersResponses = {
    /**
     * OK
     */
    200: Array<PlayedRosterResponse>;
};

export type FindPlayedRostersResponse = FindPlayedRostersResponses[keyof FindPlayedRostersResponses];

export type SecurityEventsData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: {
        /**
         * Zero-based page index (0..N)
         */
        page?: number;
        /**
         * The size of the page to be returned
         */
        size?: number;
        /**
         * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
         */
        sort?: Array<string>;
    };
    url: '/users/{userId}/security-events';
};

export type SecurityEventsErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type SecurityEventsError = SecurityEventsErrors[keyof SecurityEventsErrors];

export type SecurityEventsResponses = {
    /**
     * OK
     */
    200: SecurityEventPageResponse;
};

export type SecurityEventsResponse = SecurityEventsResponses[keyof SecurityEventsResponses];

export type ResetTwoFactorData = {
    body: ReasonRequest;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/two-factor/reset';
};

export type ResetTwoFactorErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ResetTwoFactorError = ResetTwoFactorErrors[keyof ResetTwoFactorErrors];

export type ResetTwoFactorResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ResetTwoFactorResponse = ResetTwoFactorResponses[keyof ResetTwoFactorResponses];

export type ResendReenrolmentLinkData = {
    body?: never;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/two-factor/reset/resend';
};

export type ResendReenrolmentLinkErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type ResendReenrolmentLinkError = ResendReenrolmentLinkErrors[keyof ResendReenrolmentLinkErrors];

export type ResendReenrolmentLinkResponses = {
    /**
     * No Content
     */
    204: void;
};

export type ResendReenrolmentLinkResponse = ResendReenrolmentLinkResponses[keyof ResendReenrolmentLinkResponses];

export type UnlockData = {
    body: UnlockRequest;
    path: {
        userId: number;
    };
    query?: never;
    url: '/users/{userId}/unlock';
};

export type UnlockErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type UnlockError = UnlockErrors[keyof UnlockErrors];

export type UnlockResponses = {
    /**
     * No Content
     */
    204: void;
};

export type UnlockResponse = UnlockResponses[keyof UnlockResponses];

export type VersionData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/version';
};

export type VersionErrors = {
    /**
     * Validation error
     */
    400: ApiError;
    /**
     * Unauthorized
     */
    401: ApiError;
    /**
     * Forbidden (access denied)
     */
    403: ApiError;
    /**
     * Not Found
     */
    404: ApiError;
    /**
     * Server error
     */
    500: ApiError;
};

export type VersionError = VersionErrors[keyof VersionErrors];

export type VersionResponses = {
    /**
     * OK
     */
    200: Version;
};

export type VersionResponse = VersionResponses[keyof VersionResponses];
